import * as z from 'zod';

export const CreateDataCollectionRequest = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    collection: z
      .object({
        _id: z
          .string()
          .describe(
            '[Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.\nFor [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.'
          )
          .max(256),
        collectionType: z
          .enum(['NATIVE', 'WIX_APP', 'BLOCKS_APP', 'EXTERNAL'])
          .optional(),
        ownerAppId: z
          .string()
          .describe(
            'ID of the app that defined this collection. For collections defined by Wix users, this value is null.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        maxPageSize: z
          .number()
          .int()
          .describe(
            "Maximum number of items returned in a single query, based on the underlying storage.\nNative collections have a maximum page size of 1000 for offset-based queries or 100 for cursor-based queries.\nExternal collections' maximum page size defaults to 50, but an external provider can set any maximum value up to 1000."
          )
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe(
            "Collection's display name as shown in the CMS. For example, `My First Collection`."
          )
          .max(1000)
          .optional()
          .nullable(),
        defaultDisplayOrder: z
          .object({
            fieldKey: z
              .string()
              .describe('Field to sort by.')
              .max(1000)
              .optional(),
            direction: z.enum(['ASC', 'DESC']).optional(),
          })
          .describe(
            "Default item sorting order when a query doesn't specify one."
          )
          .optional(),
        displayNamespace: z
          .string()
          .describe(
            'UI-friendly namespace of the Wix app with which the data collection is associated, such as Stores or Bookings.\nEmpty for all data collections not owned by Wix apps.'
          )
          .max(1000)
          .optional()
          .nullable(),
        displayField: z
          .string()
          .describe(
            'Field whose value the CMS displays to represent the collection item when referenced in a different collection.'
          )
          .max(1000)
          .optional()
          .nullable(),
        capabilities: z
          .object({
            dataOperations: z
              .array(
                z.enum([
                  'AGGREGATE',
                  'BULK_INSERT',
                  'BULK_REMOVE',
                  'BULK_SAVE',
                  'BULK_UPDATE',
                  'COUNT',
                  'DISTINCT',
                  'FIND',
                  'GET',
                  'INSERT',
                  'INSERT_REFERENCE',
                  'IS_REFERENCED',
                  'QUERY_REFERENCED',
                  'REMOVE',
                  'REMOVE_REFERENCE',
                  'REPLACE_REFERENCES',
                  'SAVE',
                  'TRUNCATE',
                  'UPDATE',
                  'PATCH',
                  'BULK_PATCH',
                ])
              )
              .max(100)
              .optional(),
            collectionOperations: z
              .array(z.enum(['UPDATE', 'REMOVE']))
              .max(100)
              .optional(),
            indexLimits: z
              .object({
                regular: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular (non-unique) indexes allowed for this collection.'
                  )
                  .optional(),
                unique: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of unique indexes allowed for this collection.'
                  )
                  .optional(),
                total: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular and unique indexes allowed for this collection.'
                  )
                  .optional(),
              })
              .describe('Maximum number of indexes for the collection.')
              .optional(),
          })
          .describe(
            'Capabilities the collection supports.\nCapabilities are set by default. Values depend on the collection type.'
          )
          .optional(),
        fields: z
          .array(
            z.intersection(
              z.object({
                key: z
                  .string()
                  .describe(
                    'Unique identifier for the field. For example, `firstName`.'
                  )
                  .max(1000),
                displayName: z
                  .string()
                  .describe(
                    "Field's display name when displayed in the CMS. For example, `First Name`."
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                type: z.enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ]),
                typeMetadata:
                  comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                    .describe(
                      'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                    )
                    .optional(),
                systemField: z
                  .boolean()
                  .describe('Whether the field is a system field.')
                  .optional(),
                capabilities: z
                  .object({
                    sortable: z
                      .boolean()
                      .describe(
                        'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryOperators: z
                      .array(
                        z.enum([
                          'EQ',
                          'LT',
                          'GT',
                          'NE',
                          'LTE',
                          'GTE',
                          'STARTS_WITH',
                          'ENDS_WITH',
                          'CONTAINS',
                          'HAS_SOME',
                          'HAS_ALL',
                          'EXISTS',
                          'URLIZED',
                        ])
                      )
                      .max(100)
                      .optional(),
                  })
                  .describe('Capabilities the field supports.')
                  .optional(),
                encrypted: z
                  .boolean()
                  .describe('Whether the field is encrypted.')
                  .optional(),
                description: z
                  .string()
                  .describe('Field description.')
                  .max(16000)
                  .optional()
                  .nullable(),
                readOnly: z
                  .boolean()
                  .describe(
                    "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
                  )
                  .optional()
                  .nullable(),
                immutable: z
                  .boolean()
                  .describe(
                    'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                required: z
                  .boolean()
                  .describe(
                    'Whether the field is required.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                plugins: z
                  .array(
                    z.intersection(
                      z.object({ type: z.enum(['UNKNOWN', 'CMS']).optional() }),
                      z.xor([
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z.never().optional(),
                        }),
                        z.object({
                          categoryFieldOptions: z.never().optional(),
                          cmsOptions: z
                            .object({
                              internal: z
                                .boolean()
                                .describe(
                                  'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                                )
                                .optional(),
                            })
                            .describe('Options for the CMS plugin.'),
                        }),
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z
                            .object({
                              isPrimary: z
                                .boolean()
                                .describe(
                                  "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                                )
                                .optional(),
                            })
                            .describe('Options for the CATEGORY_FIELD plugin.'),
                        }),
                      ])
                    )
                  )
                  .max(100)
                  .optional(),
                condition: z
                  .object({
                    groupName: z
                      .string()
                      .describe(
                        'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    permission: z.enum(['ANYONE', 'RESTRICTED']).optional(),
                  })
                  .describe(
                    'This indicates that this fields is conditional and is not retrieved by default.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                }),
                z.object({
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  numberRange: z
                    .object({
                      min: z
                        .number()
                        .describe(
                          'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      max: z
                        .number()
                        .describe(
                          'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Range of possible values for a numerical field.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  stringLengthRange: z
                    .object({
                      minLength: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxLength: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z
                    .object({
                      minSize: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxSize: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
                    ),
                }),
              ])
            )
          )
          .max(1000)
          .optional(),
        permissions: z
          .object({
            insert: z.enum([
              'ADMIN',
              'SITE_MEMBER_AUTHOR',
              'SITE_MEMBER',
              'ANYONE',
            ]),
            update: z.enum([
              'ADMIN',
              'SITE_MEMBER_AUTHOR',
              'SITE_MEMBER',
              'ANYONE',
            ]),
            remove: z.enum([
              'ADMIN',
              'SITE_MEMBER_AUTHOR',
              'SITE_MEMBER',
              'ANYONE',
            ]),
            read: z.enum([
              'ADMIN',
              'SITE_MEMBER_AUTHOR',
              'SITE_MEMBER',
              'ANYONE',
            ]),
          })
          .describe(
            '[Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest role needed to perform each action.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            "Collection's current revision number, which increments each time the collection is updated. For an update operation to succeed, you must specify the latest revision number."
          )
          .optional()
          .nullable(),
        plugins: z
          .array(
            z.intersection(
              z.object({
                type: z
                  .enum([
                    'PUBLISH',
                    'SINGLE_ITEM',
                    'URLIZED',
                    'GRIDAPPLESS',
                    'MULTILINGUAL',
                    'SHARED',
                    'EDITABLE_PAGE_LINK',
                    'CMS',
                  ])
                  .optional(),
              }),
              z.xor([
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                }),
                z.object({
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  publishOptions: z
                    .object({
                      defaultStatus: z
                        .enum(['PUBLISHED', 'DRAFT'])
                        .describe('Default status.')
                        .optional(),
                    })
                    .describe(
                      'Options for the Publish plugin.\nThis plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.'
                    ),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  singleItemOptions: z
                    .object({
                      singleItemId: z
                        .string()
                        .describe(
                          'ID of the single item in this collection. If you insert or update an item, its ID value is always changed to this.'
                        )
                        .max(16000)
                        .optional(),
                    })
                    .describe('Options for the Single Item plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  urlizedOptions: z
                    .object({
                      format: z
                        .enum(['ORIGINAL', 'PLAIN'])
                        .describe(
                          'Encoding method for generating a URL in ASCII characters.'
                        )
                        .optional(),
                    })
                    .describe('Options for the Urlized plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  multilingualOptions: z
                    .object({
                      translatableFieldKeys: z
                        .array(z.string())
                        .max(1000)
                        .optional(),
                    })
                    .describe('Options for the Multilingual plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  editablePageLinkOptions: z
                    .object({
                      isPersisted: z.boolean().optional(),
                      isMutable: z.boolean().optional(),
                    })
                    .describe('Options for the PageLink plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z
                    .object({
                      siteSort: z
                        .object({
                          sort: z
                            .array(
                              z.object({
                                fieldKey: z
                                  .string()
                                  .describe('Field to sort by.')
                                  .max(1000)
                                  .optional(),
                                direction: z.enum(['ASC', 'DESC']).optional(),
                              })
                            )
                            .max(100)
                            .optional(),
                        })
                        .describe(
                          'CMS sort, applied when a collection is displayed on a site.'
                        )
                        .optional(),
                    })
                    .describe('Options for the CMS plugin.'),
                }),
              ])
            )
          )
          .max(100)
          .optional(),
        pagingModes: z
          .array(z.enum(['OFFSET', 'CURSOR']))
          .max(2)
          .optional(),
        _createdDate: z
          .date()
          .describe('Date the collection was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date the collection was last updated.')
          .optional()
          .nullable(),
      })
      .describe('Collection details.'),
  });
})();
export const CreateDataCollectionResponse = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .describe(
                        'Field type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data).'
                      )
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    _id: z
      .string()
      .describe(
        '[Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.\nFor [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.'
      )
      .max(256)
      .optional(),
    collectionType: z
      .enum(['NATIVE', 'WIX_APP', 'BLOCKS_APP', 'EXTERNAL'])
      .describe(
        'Collection type. Indicates how the collection was created and how it is stored.'
      )
      .optional(),
    ownerAppId: z
      .string()
      .describe(
        'ID of the app that defined this collection. For collections defined by Wix users, this value is null.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    maxPageSize: z
      .number()
      .int()
      .describe(
        "Maximum number of items returned in a single query, based on the underlying storage.\nNative collections have a maximum page size of 1000 for offset-based queries or 100 for cursor-based queries.\nExternal collections' maximum page size defaults to 50, but an external provider can set any maximum value up to 1000."
      )
      .optional()
      .nullable(),
    displayName: z
      .string()
      .describe(
        "Collection's display name as shown in the CMS. For example, `My First Collection`."
      )
      .max(1000)
      .optional()
      .nullable(),
    defaultDisplayOrder: z
      .object({
        fieldKey: z.string().describe('Field to sort by.').max(1000).optional(),
        direction: z
          .enum(['ASC', 'DESC'])
          .describe(
            'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
          )
          .optional(),
      })
      .describe("Default item sorting order when a query doesn't specify one.")
      .optional(),
    displayNamespace: z
      .string()
      .describe(
        'UI-friendly namespace of the Wix app with which the data collection is associated, such as Stores or Bookings.\nEmpty for all data collections not owned by Wix apps.'
      )
      .max(1000)
      .optional()
      .nullable(),
    displayField: z
      .string()
      .describe(
        'Field whose value the CMS displays to represent the collection item when referenced in a different collection.'
      )
      .max(1000)
      .optional()
      .nullable(),
    capabilities: z
      .object({
        dataOperations: z
          .array(
            z.enum([
              'AGGREGATE',
              'BULK_INSERT',
              'BULK_REMOVE',
              'BULK_SAVE',
              'BULK_UPDATE',
              'COUNT',
              'DISTINCT',
              'FIND',
              'GET',
              'INSERT',
              'INSERT_REFERENCE',
              'IS_REFERENCED',
              'QUERY_REFERENCED',
              'REMOVE',
              'REMOVE_REFERENCE',
              'REPLACE_REFERENCES',
              'SAVE',
              'TRUNCATE',
              'UPDATE',
              'PATCH',
              'BULK_PATCH',
            ])
          )
          .max(100)
          .optional(),
        collectionOperations: z
          .array(z.enum(['UPDATE', 'REMOVE']))
          .max(100)
          .optional(),
        indexLimits: z
          .object({
            regular: z
              .number()
              .int()
              .describe(
                'Maximum number of regular (non-unique) indexes allowed for this collection.'
              )
              .optional(),
            unique: z
              .number()
              .int()
              .describe(
                'Maximum number of unique indexes allowed for this collection.'
              )
              .optional(),
            total: z
              .number()
              .int()
              .describe(
                'Maximum number of regular and unique indexes allowed for this collection.'
              )
              .optional(),
          })
          .describe('Maximum number of indexes for the collection.')
          .optional(),
      })
      .describe(
        'Capabilities the collection supports.\nCapabilities are set by default. Values depend on the collection type.'
      )
      .optional(),
    fields: z
      .array(
        z.intersection(
          z.object({
            key: z
              .string()
              .describe(
                'Unique identifier for the field. For example, `firstName`.'
              )
              .max(1000)
              .optional(),
            displayName: z
              .string()
              .describe(
                "Field's display name when displayed in the CMS. For example, `First Name`."
              )
              .max(1000)
              .optional()
              .nullable(),
            type: z
              .enum([
                'TEXT',
                'NUMBER',
                'DATE',
                'DATETIME',
                'IMAGE',
                'BOOLEAN',
                'DOCUMENT',
                'URL',
                'RICH_TEXT',
                'VIDEO',
                'ANY',
                'ARRAY_STRING',
                'ARRAY_DOCUMENT',
                'AUDIO',
                'TIME',
                'LANGUAGE',
                'RICH_CONTENT',
                'MEDIA_GALLERY',
                'ADDRESS',
                'PAGE_LINK',
                'REFERENCE',
                'MULTI_REFERENCE',
                'OBJECT',
                'ARRAY',
                'LEGACY_TIME',
                'LEGACY_BOOK',
                'LEGACY_EXTERNAL_URL',
                'LEGACY_BROKEN_REFERENCE',
                'LEGACY_IMAGE',
                'LEGACY_COLOR',
                'LEGACY_EXTERNAL_VIDEO',
                'EMAIL',
              ])
              .describe(
                "Field's data type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data)."
              )
              .optional(),
            typeMetadata:
              comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            systemField: z
              .boolean()
              .describe('Whether the field is a system field.')
              .optional(),
            capabilities: z
              .object({
                sortable: z
                  .boolean()
                  .describe(
                    'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                  )
                  .optional(),
                queryOperators: z
                  .array(
                    z.enum([
                      'EQ',
                      'LT',
                      'GT',
                      'NE',
                      'LTE',
                      'GTE',
                      'STARTS_WITH',
                      'ENDS_WITH',
                      'CONTAINS',
                      'HAS_SOME',
                      'HAS_ALL',
                      'EXISTS',
                      'URLIZED',
                    ])
                  )
                  .max(100)
                  .optional(),
              })
              .describe('Capabilities the field supports.')
              .optional(),
            encrypted: z
              .boolean()
              .describe('Whether the field is encrypted.')
              .optional(),
            description: z
              .string()
              .describe('Field description.')
              .max(16000)
              .optional()
              .nullable(),
            readOnly: z
              .boolean()
              .describe(
                "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
              )
              .optional()
              .nullable(),
            immutable: z
              .boolean()
              .describe(
                'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
              )
              .optional()
              .nullable(),
            required: z
              .boolean()
              .describe('Whether the field is required.\n\nDefault: `false`')
              .optional()
              .nullable(),
            plugins: z
              .array(
                z.intersection(
                  z.object({ type: z.enum(['UNKNOWN', 'CMS']).optional() }),
                  z.xor([
                    z.object({
                      cmsOptions: z.never().optional(),
                      categoryFieldOptions: z.never().optional(),
                    }),
                    z.object({
                      categoryFieldOptions: z.never().optional(),
                      cmsOptions: z
                        .object({
                          internal: z
                            .boolean()
                            .describe(
                              'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                            )
                            .optional(),
                        })
                        .describe('Options for the CMS plugin.'),
                    }),
                    z.object({
                      cmsOptions: z.never().optional(),
                      categoryFieldOptions: z
                        .object({
                          isPrimary: z
                            .boolean()
                            .describe(
                              "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                            )
                            .optional(),
                        })
                        .describe('Options for the CATEGORY_FIELD plugin.'),
                    }),
                  ])
                )
              )
              .max(100)
              .optional(),
            condition: z
              .object({
                groupName: z
                  .string()
                  .describe(
                    'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                permission: z
                  .enum(['ANYONE', 'RESTRICTED'])
                  .describe(
                    'If present, the field may be omitted from the results, if the requester lacks sufficient permissions to access data stored in the field.'
                  )
                  .optional(),
              })
              .describe(
                'This indicates that this fields is conditional and is not retrieved by default.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              numberRange: z.never().optional(),
              stringLengthRange: z.never().optional(),
              arraySizeRange: z.never().optional(),
            }),
            z.object({
              stringLengthRange: z.never().optional(),
              arraySizeRange: z.never().optional(),
              numberRange: z
                .object({
                  min: z
                    .number()
                    .describe(
                      'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                  max: z
                    .number()
                    .describe(
                      'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Range of possible values for a numerical field.'),
            }),
            z.object({
              numberRange: z.never().optional(),
              arraySizeRange: z.never().optional(),
              stringLengthRange: z
                .object({
                  minLength: z
                    .number()
                    .int()
                    .describe(
                      'Minimum permitted length for a text field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                  maxLength: z
                    .number()
                    .int()
                    .describe(
                      'Maximum permitted length for a text field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
                ),
            }),
            z.object({
              numberRange: z.never().optional(),
              stringLengthRange: z.never().optional(),
              arraySizeRange: z
                .object({
                  minSize: z
                    .number()
                    .int()
                    .describe(
                      'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                  maxSize: z
                    .number()
                    .int()
                    .describe(
                      'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
                ),
            }),
          ])
        )
      )
      .max(1000)
      .optional(),
    permissions: z
      .object({
        insert: z
          .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
          .describe(
            'Role required to insert items into the collection.\n\nDefault: `CMS_EDITOR`'
          )
          .optional(),
        update: z
          .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
          .describe(
            'Role required to update collection items.\n\nDefault: `CMS_EDITOR`'
          )
          .optional(),
        remove: z
          .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
          .describe(
            'Role required to remove items from the collection.\n\nDefault: `CMS_EDITOR`'
          )
          .optional(),
        read: z
          .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
          .describe(
            'Role required to read collection items.\n\nDefault: `SITE_MEMBER_AUTHOR`'
          )
          .optional(),
      })
      .describe(
        '[Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest role needed to perform each action.'
      )
      .optional(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        "Collection's current revision number, which increments each time the collection is updated. For an update operation to succeed, you must specify the latest revision number."
      )
      .optional()
      .nullable(),
    plugins: z
      .array(
        z.intersection(
          z.object({
            type: z
              .enum([
                'PUBLISH',
                'SINGLE_ITEM',
                'URLIZED',
                'GRIDAPPLESS',
                'MULTILINGUAL',
                'SHARED',
                'EDITABLE_PAGE_LINK',
                'CMS',
              ])
              .describe('Plugin type.')
              .optional(),
          }),
          z.xor([
            z.object({
              publishOptions: z.never().optional(),
              singleItemOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
            }),
            z.object({
              singleItemOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
              publishOptions: z
                .object({
                  defaultStatus: z
                    .enum(['PUBLISHED', 'DRAFT'])
                    .describe('Default status.')
                    .optional(),
                })
                .describe(
                  'Options for the Publish plugin.\nThis plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.'
                ),
            }),
            z.object({
              publishOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
              singleItemOptions: z
                .object({
                  singleItemId: z
                    .string()
                    .describe(
                      'ID of the single item in this collection. If you insert or update an item, its ID value is always changed to this.'
                    )
                    .max(16000)
                    .optional(),
                })
                .describe('Options for the Single Item plugin.'),
            }),
            z.object({
              publishOptions: z.never().optional(),
              singleItemOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
              urlizedOptions: z
                .object({
                  format: z
                    .enum(['ORIGINAL', 'PLAIN'])
                    .describe(
                      'Encoding method for generating a URL in ASCII characters.'
                    )
                    .optional(),
                })
                .describe('Options for the Urlized plugin.'),
            }),
            z.object({
              publishOptions: z.never().optional(),
              singleItemOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
              multilingualOptions: z
                .object({
                  translatableFieldKeys: z
                    .array(z.string())
                    .max(1000)
                    .optional(),
                })
                .describe('Options for the Multilingual plugin.'),
            }),
            z.object({
              publishOptions: z.never().optional(),
              singleItemOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
              editablePageLinkOptions: z
                .object({
                  isPersisted: z.boolean().optional(),
                  isMutable: z.boolean().optional(),
                })
                .describe('Options for the PageLink plugin.'),
            }),
            z.object({
              publishOptions: z.never().optional(),
              singleItemOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z
                .object({
                  siteSort: z
                    .object({
                      sort: z
                        .array(
                          z.object({
                            fieldKey: z
                              .string()
                              .describe('Field to sort by.')
                              .max(1000)
                              .optional(),
                            direction: z
                              .enum(['ASC', 'DESC'])
                              .describe(
                                'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
                              )
                              .optional(),
                          })
                        )
                        .max(100)
                        .optional(),
                    })
                    .describe(
                      'CMS sort, applied when a collection is displayed on a site.'
                    )
                    .optional(),
                })
                .describe('Options for the CMS plugin.'),
            }),
          ])
        )
      )
      .max(100)
      .optional(),
    pagingModes: z
      .array(z.enum(['OFFSET', 'CURSOR']))
      .max(2)
      .optional(),
    _createdDate: z
      .date()
      .describe('Date the collection was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date the collection was last updated.')
      .optional()
      .nullable(),
  });
})();
export const GetDataCollectionRequest = z.object({
  dataCollectionId: z
    .string()
    .describe('ID of the collection to retrieve.')
    .max(256),
  options: z
    .object({
      consistentRead: z
        .boolean()
        .describe(
          'Whether to retrieve data from the primary database instance.\nThis decreases performance but ensures data retrieved is up to date even immediately after an update.\nLearn more about [Wix Data and eventual consistency](https://dev.wix.com/api/rest/wix-data/wix-data/eventual-consistency).\n\nDefault: `false`'
        )
        .optional(),
      fields: z.array(z.string()).max(100).optional(),
    })
    .describe('Options for retrieving a data collection.')
    .optional(),
});
export const GetDataCollectionResponse = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .describe(
                        'Field type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data).'
                      )
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    _id: z
      .string()
      .describe(
        '[Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.\nFor [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.'
      )
      .max(256)
      .optional(),
    collectionType: z
      .enum(['NATIVE', 'WIX_APP', 'BLOCKS_APP', 'EXTERNAL'])
      .describe(
        'Collection type. Indicates how the collection was created and how it is stored.'
      )
      .optional(),
    ownerAppId: z
      .string()
      .describe(
        'ID of the app that defined this collection. For collections defined by Wix users, this value is null.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    maxPageSize: z
      .number()
      .int()
      .describe(
        "Maximum number of items returned in a single query, based on the underlying storage.\nNative collections have a maximum page size of 1000 for offset-based queries or 100 for cursor-based queries.\nExternal collections' maximum page size defaults to 50, but an external provider can set any maximum value up to 1000."
      )
      .optional()
      .nullable(),
    displayName: z
      .string()
      .describe(
        "Collection's display name as shown in the CMS. For example, `My First Collection`."
      )
      .max(1000)
      .optional()
      .nullable(),
    defaultDisplayOrder: z
      .object({
        fieldKey: z.string().describe('Field to sort by.').max(1000).optional(),
        direction: z
          .enum(['ASC', 'DESC'])
          .describe(
            'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
          )
          .optional(),
      })
      .describe("Default item sorting order when a query doesn't specify one.")
      .optional(),
    displayNamespace: z
      .string()
      .describe(
        'UI-friendly namespace of the Wix app with which the data collection is associated, such as Stores or Bookings.\nEmpty for all data collections not owned by Wix apps.'
      )
      .max(1000)
      .optional()
      .nullable(),
    displayField: z
      .string()
      .describe(
        'Field whose value the CMS displays to represent the collection item when referenced in a different collection.'
      )
      .max(1000)
      .optional()
      .nullable(),
    capabilities: z
      .object({
        dataOperations: z
          .array(
            z.enum([
              'AGGREGATE',
              'BULK_INSERT',
              'BULK_REMOVE',
              'BULK_SAVE',
              'BULK_UPDATE',
              'COUNT',
              'DISTINCT',
              'FIND',
              'GET',
              'INSERT',
              'INSERT_REFERENCE',
              'IS_REFERENCED',
              'QUERY_REFERENCED',
              'REMOVE',
              'REMOVE_REFERENCE',
              'REPLACE_REFERENCES',
              'SAVE',
              'TRUNCATE',
              'UPDATE',
              'PATCH',
              'BULK_PATCH',
            ])
          )
          .max(100)
          .optional(),
        collectionOperations: z
          .array(z.enum(['UPDATE', 'REMOVE']))
          .max(100)
          .optional(),
        indexLimits: z
          .object({
            regular: z
              .number()
              .int()
              .describe(
                'Maximum number of regular (non-unique) indexes allowed for this collection.'
              )
              .optional(),
            unique: z
              .number()
              .int()
              .describe(
                'Maximum number of unique indexes allowed for this collection.'
              )
              .optional(),
            total: z
              .number()
              .int()
              .describe(
                'Maximum number of regular and unique indexes allowed for this collection.'
              )
              .optional(),
          })
          .describe('Maximum number of indexes for the collection.')
          .optional(),
      })
      .describe(
        'Capabilities the collection supports.\nCapabilities are set by default. Values depend on the collection type.'
      )
      .optional(),
    fields: z
      .array(
        z.intersection(
          z.object({
            key: z
              .string()
              .describe(
                'Unique identifier for the field. For example, `firstName`.'
              )
              .max(1000)
              .optional(),
            displayName: z
              .string()
              .describe(
                "Field's display name when displayed in the CMS. For example, `First Name`."
              )
              .max(1000)
              .optional()
              .nullable(),
            type: z
              .enum([
                'TEXT',
                'NUMBER',
                'DATE',
                'DATETIME',
                'IMAGE',
                'BOOLEAN',
                'DOCUMENT',
                'URL',
                'RICH_TEXT',
                'VIDEO',
                'ANY',
                'ARRAY_STRING',
                'ARRAY_DOCUMENT',
                'AUDIO',
                'TIME',
                'LANGUAGE',
                'RICH_CONTENT',
                'MEDIA_GALLERY',
                'ADDRESS',
                'PAGE_LINK',
                'REFERENCE',
                'MULTI_REFERENCE',
                'OBJECT',
                'ARRAY',
                'LEGACY_TIME',
                'LEGACY_BOOK',
                'LEGACY_EXTERNAL_URL',
                'LEGACY_BROKEN_REFERENCE',
                'LEGACY_IMAGE',
                'LEGACY_COLOR',
                'LEGACY_EXTERNAL_VIDEO',
                'EMAIL',
              ])
              .describe(
                "Field's data type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data)."
              )
              .optional(),
            typeMetadata:
              comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            systemField: z
              .boolean()
              .describe('Whether the field is a system field.')
              .optional(),
            capabilities: z
              .object({
                sortable: z
                  .boolean()
                  .describe(
                    'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                  )
                  .optional(),
                queryOperators: z
                  .array(
                    z.enum([
                      'EQ',
                      'LT',
                      'GT',
                      'NE',
                      'LTE',
                      'GTE',
                      'STARTS_WITH',
                      'ENDS_WITH',
                      'CONTAINS',
                      'HAS_SOME',
                      'HAS_ALL',
                      'EXISTS',
                      'URLIZED',
                    ])
                  )
                  .max(100)
                  .optional(),
              })
              .describe('Capabilities the field supports.')
              .optional(),
            encrypted: z
              .boolean()
              .describe('Whether the field is encrypted.')
              .optional(),
            description: z
              .string()
              .describe('Field description.')
              .max(16000)
              .optional()
              .nullable(),
            readOnly: z
              .boolean()
              .describe(
                "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
              )
              .optional()
              .nullable(),
            immutable: z
              .boolean()
              .describe(
                'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
              )
              .optional()
              .nullable(),
            required: z
              .boolean()
              .describe('Whether the field is required.\n\nDefault: `false`')
              .optional()
              .nullable(),
            plugins: z
              .array(
                z.intersection(
                  z.object({ type: z.enum(['UNKNOWN', 'CMS']).optional() }),
                  z.xor([
                    z.object({
                      cmsOptions: z.never().optional(),
                      categoryFieldOptions: z.never().optional(),
                    }),
                    z.object({
                      categoryFieldOptions: z.never().optional(),
                      cmsOptions: z
                        .object({
                          internal: z
                            .boolean()
                            .describe(
                              'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                            )
                            .optional(),
                        })
                        .describe('Options for the CMS plugin.'),
                    }),
                    z.object({
                      cmsOptions: z.never().optional(),
                      categoryFieldOptions: z
                        .object({
                          isPrimary: z
                            .boolean()
                            .describe(
                              "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                            )
                            .optional(),
                        })
                        .describe('Options for the CATEGORY_FIELD plugin.'),
                    }),
                  ])
                )
              )
              .max(100)
              .optional(),
            condition: z
              .object({
                groupName: z
                  .string()
                  .describe(
                    'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                permission: z
                  .enum(['ANYONE', 'RESTRICTED'])
                  .describe(
                    'If present, the field may be omitted from the results, if the requester lacks sufficient permissions to access data stored in the field.'
                  )
                  .optional(),
              })
              .describe(
                'This indicates that this fields is conditional and is not retrieved by default.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              numberRange: z.never().optional(),
              stringLengthRange: z.never().optional(),
              arraySizeRange: z.never().optional(),
            }),
            z.object({
              stringLengthRange: z.never().optional(),
              arraySizeRange: z.never().optional(),
              numberRange: z
                .object({
                  min: z
                    .number()
                    .describe(
                      'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                  max: z
                    .number()
                    .describe(
                      'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Range of possible values for a numerical field.'),
            }),
            z.object({
              numberRange: z.never().optional(),
              arraySizeRange: z.never().optional(),
              stringLengthRange: z
                .object({
                  minLength: z
                    .number()
                    .int()
                    .describe(
                      'Minimum permitted length for a text field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                  maxLength: z
                    .number()
                    .int()
                    .describe(
                      'Maximum permitted length for a text field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
                ),
            }),
            z.object({
              numberRange: z.never().optional(),
              stringLengthRange: z.never().optional(),
              arraySizeRange: z
                .object({
                  minSize: z
                    .number()
                    .int()
                    .describe(
                      'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                  maxSize: z
                    .number()
                    .int()
                    .describe(
                      'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
                ),
            }),
          ])
        )
      )
      .max(1000)
      .optional(),
    permissions: z
      .object({
        insert: z
          .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
          .describe(
            'Role required to insert items into the collection.\n\nDefault: `CMS_EDITOR`'
          )
          .optional(),
        update: z
          .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
          .describe(
            'Role required to update collection items.\n\nDefault: `CMS_EDITOR`'
          )
          .optional(),
        remove: z
          .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
          .describe(
            'Role required to remove items from the collection.\n\nDefault: `CMS_EDITOR`'
          )
          .optional(),
        read: z
          .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
          .describe(
            'Role required to read collection items.\n\nDefault: `SITE_MEMBER_AUTHOR`'
          )
          .optional(),
      })
      .describe(
        '[Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest role needed to perform each action.'
      )
      .optional(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        "Collection's current revision number, which increments each time the collection is updated. For an update operation to succeed, you must specify the latest revision number."
      )
      .optional()
      .nullable(),
    plugins: z
      .array(
        z.intersection(
          z.object({
            type: z
              .enum([
                'PUBLISH',
                'SINGLE_ITEM',
                'URLIZED',
                'GRIDAPPLESS',
                'MULTILINGUAL',
                'SHARED',
                'EDITABLE_PAGE_LINK',
                'CMS',
              ])
              .describe('Plugin type.')
              .optional(),
          }),
          z.xor([
            z.object({
              publishOptions: z.never().optional(),
              singleItemOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
            }),
            z.object({
              singleItemOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
              publishOptions: z
                .object({
                  defaultStatus: z
                    .enum(['PUBLISHED', 'DRAFT'])
                    .describe('Default status.')
                    .optional(),
                })
                .describe(
                  'Options for the Publish plugin.\nThis plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.'
                ),
            }),
            z.object({
              publishOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
              singleItemOptions: z
                .object({
                  singleItemId: z
                    .string()
                    .describe(
                      'ID of the single item in this collection. If you insert or update an item, its ID value is always changed to this.'
                    )
                    .max(16000)
                    .optional(),
                })
                .describe('Options for the Single Item plugin.'),
            }),
            z.object({
              publishOptions: z.never().optional(),
              singleItemOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
              urlizedOptions: z
                .object({
                  format: z
                    .enum(['ORIGINAL', 'PLAIN'])
                    .describe(
                      'Encoding method for generating a URL in ASCII characters.'
                    )
                    .optional(),
                })
                .describe('Options for the Urlized plugin.'),
            }),
            z.object({
              publishOptions: z.never().optional(),
              singleItemOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
              multilingualOptions: z
                .object({
                  translatableFieldKeys: z
                    .array(z.string())
                    .max(1000)
                    .optional(),
                })
                .describe('Options for the Multilingual plugin.'),
            }),
            z.object({
              publishOptions: z.never().optional(),
              singleItemOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
              editablePageLinkOptions: z
                .object({
                  isPersisted: z.boolean().optional(),
                  isMutable: z.boolean().optional(),
                })
                .describe('Options for the PageLink plugin.'),
            }),
            z.object({
              publishOptions: z.never().optional(),
              singleItemOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z
                .object({
                  siteSort: z
                    .object({
                      sort: z
                        .array(
                          z.object({
                            fieldKey: z
                              .string()
                              .describe('Field to sort by.')
                              .max(1000)
                              .optional(),
                            direction: z
                              .enum(['ASC', 'DESC'])
                              .describe(
                                'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
                              )
                              .optional(),
                          })
                        )
                        .max(100)
                        .optional(),
                    })
                    .describe(
                      'CMS sort, applied when a collection is displayed on a site.'
                    )
                    .optional(),
                })
                .describe('Options for the CMS plugin.'),
            }),
          ])
        )
      )
      .max(100)
      .optional(),
    pagingModes: z
      .array(z.enum(['OFFSET', 'CURSOR']))
      .max(2)
      .optional(),
    _createdDate: z
      .date()
      .describe('Date the collection was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date the collection was last updated.')
      .optional()
      .nullable(),
  });
})();
export const ListDataCollectionsRequest = z.object({
  options: z
    .object({
      sort: z
        .object({
          fieldName: z
            .string()
            .describe('Name of the field to sort by.')
            .max(1000)
            .optional(),
          order: z.enum(['ASC', 'DESC']).optional(),
        })
        .describe(
          'Defines how collections in the response are sorted.\n\nDefault: Ordered by ID in ascending order.'
        )
        .optional(),
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load.')
            .min(0)
            .optional()
            .nullable(),
          offset: z
            .number()
            .int()
            .describe('Number of items to skip in the current sort order.')
            .min(0)
            .optional()
            .nullable(),
        })
        .describe('Pagination information.')
        .optional(),
      consistentRead: z
        .boolean()
        .describe(
          'Whether to retrieve data from the primary database instance.\nThis decreases performance but ensures data retrieved is up to date even immediately after an update.\nLearn more about [Wix Data and eventual consistency](https://dev.wix.com/api/rest/wix-data/wix-data/eventual-consistency).\n\nDefault: `false`'
        )
        .optional(),
      fields: z.array(z.string()).max(100).optional(),
    })
    .describe('Options for retrieving a list of data collections.')
    .optional(),
});
export const ListDataCollectionsResponse = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .describe(
                        'Field type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data).'
                      )
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    collections: z
      .array(
        z.object({
          _id: z
            .string()
            .describe(
              '[Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.\nFor [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.'
            )
            .max(256)
            .optional(),
          collectionType: z
            .enum(['NATIVE', 'WIX_APP', 'BLOCKS_APP', 'EXTERNAL'])
            .describe(
              'Collection type. Indicates how the collection was created and how it is stored.'
            )
            .optional(),
          ownerAppId: z
            .string()
            .describe(
              'ID of the app that defined this collection. For collections defined by Wix users, this value is null.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          maxPageSize: z
            .number()
            .int()
            .describe(
              "Maximum number of items returned in a single query, based on the underlying storage.\nNative collections have a maximum page size of 1000 for offset-based queries or 100 for cursor-based queries.\nExternal collections' maximum page size defaults to 50, but an external provider can set any maximum value up to 1000."
            )
            .optional()
            .nullable(),
          displayName: z
            .string()
            .describe(
              "Collection's display name as shown in the CMS. For example, `My First Collection`."
            )
            .max(1000)
            .optional()
            .nullable(),
          defaultDisplayOrder: z
            .object({
              fieldKey: z
                .string()
                .describe('Field to sort by.')
                .max(1000)
                .optional(),
              direction: z
                .enum(['ASC', 'DESC'])
                .describe(
                  'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
                )
                .optional(),
            })
            .describe(
              "Default item sorting order when a query doesn't specify one."
            )
            .optional(),
          displayNamespace: z
            .string()
            .describe(
              'UI-friendly namespace of the Wix app with which the data collection is associated, such as Stores or Bookings.\nEmpty for all data collections not owned by Wix apps.'
            )
            .max(1000)
            .optional()
            .nullable(),
          displayField: z
            .string()
            .describe(
              'Field whose value the CMS displays to represent the collection item when referenced in a different collection.'
            )
            .max(1000)
            .optional()
            .nullable(),
          capabilities: z
            .object({
              dataOperations: z
                .array(
                  z.enum([
                    'AGGREGATE',
                    'BULK_INSERT',
                    'BULK_REMOVE',
                    'BULK_SAVE',
                    'BULK_UPDATE',
                    'COUNT',
                    'DISTINCT',
                    'FIND',
                    'GET',
                    'INSERT',
                    'INSERT_REFERENCE',
                    'IS_REFERENCED',
                    'QUERY_REFERENCED',
                    'REMOVE',
                    'REMOVE_REFERENCE',
                    'REPLACE_REFERENCES',
                    'SAVE',
                    'TRUNCATE',
                    'UPDATE',
                    'PATCH',
                    'BULK_PATCH',
                  ])
                )
                .max(100)
                .optional(),
              collectionOperations: z
                .array(z.enum(['UPDATE', 'REMOVE']))
                .max(100)
                .optional(),
              indexLimits: z
                .object({
                  regular: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of regular (non-unique) indexes allowed for this collection.'
                    )
                    .optional(),
                  unique: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of unique indexes allowed for this collection.'
                    )
                    .optional(),
                  total: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of regular and unique indexes allowed for this collection.'
                    )
                    .optional(),
                })
                .describe('Maximum number of indexes for the collection.')
                .optional(),
            })
            .describe(
              'Capabilities the collection supports.\nCapabilities are set by default. Values depend on the collection type.'
            )
            .optional(),
          fields: z
            .array(
              z.intersection(
                z.object({
                  key: z
                    .string()
                    .describe(
                      'Unique identifier for the field. For example, `firstName`.'
                    )
                    .max(1000)
                    .optional(),
                  displayName: z
                    .string()
                    .describe(
                      "Field's display name when displayed in the CMS. For example, `First Name`."
                    )
                    .max(1000)
                    .optional()
                    .nullable(),
                  type: z
                    .enum([
                      'TEXT',
                      'NUMBER',
                      'DATE',
                      'DATETIME',
                      'IMAGE',
                      'BOOLEAN',
                      'DOCUMENT',
                      'URL',
                      'RICH_TEXT',
                      'VIDEO',
                      'ANY',
                      'ARRAY_STRING',
                      'ARRAY_DOCUMENT',
                      'AUDIO',
                      'TIME',
                      'LANGUAGE',
                      'RICH_CONTENT',
                      'MEDIA_GALLERY',
                      'ADDRESS',
                      'PAGE_LINK',
                      'REFERENCE',
                      'MULTI_REFERENCE',
                      'OBJECT',
                      'ARRAY',
                      'LEGACY_TIME',
                      'LEGACY_BOOK',
                      'LEGACY_EXTERNAL_URL',
                      'LEGACY_BROKEN_REFERENCE',
                      'LEGACY_IMAGE',
                      'LEGACY_COLOR',
                      'LEGACY_EXTERNAL_VIDEO',
                      'EMAIL',
                    ])
                    .describe(
                      "Field's data type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data)."
                    )
                    .optional(),
                  typeMetadata:
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                  systemField: z
                    .boolean()
                    .describe('Whether the field is a system field.')
                    .optional(),
                  capabilities: z
                    .object({
                      sortable: z
                        .boolean()
                        .describe(
                          'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                        )
                        .optional(),
                      queryOperators: z
                        .array(
                          z.enum([
                            'EQ',
                            'LT',
                            'GT',
                            'NE',
                            'LTE',
                            'GTE',
                            'STARTS_WITH',
                            'ENDS_WITH',
                            'CONTAINS',
                            'HAS_SOME',
                            'HAS_ALL',
                            'EXISTS',
                            'URLIZED',
                          ])
                        )
                        .max(100)
                        .optional(),
                    })
                    .describe('Capabilities the field supports.')
                    .optional(),
                  encrypted: z
                    .boolean()
                    .describe('Whether the field is encrypted.')
                    .optional(),
                  description: z
                    .string()
                    .describe('Field description.')
                    .max(16000)
                    .optional()
                    .nullable(),
                  readOnly: z
                    .boolean()
                    .describe(
                      "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
                    )
                    .optional()
                    .nullable(),
                  immutable: z
                    .boolean()
                    .describe(
                      'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                  required: z
                    .boolean()
                    .describe(
                      'Whether the field is required.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                  plugins: z
                    .array(
                      z.intersection(
                        z.object({
                          type: z.enum(['UNKNOWN', 'CMS']).optional(),
                        }),
                        z.xor([
                          z.object({
                            cmsOptions: z.never().optional(),
                            categoryFieldOptions: z.never().optional(),
                          }),
                          z.object({
                            categoryFieldOptions: z.never().optional(),
                            cmsOptions: z
                              .object({
                                internal: z
                                  .boolean()
                                  .describe(
                                    'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                                  )
                                  .optional(),
                              })
                              .describe('Options for the CMS plugin.'),
                          }),
                          z.object({
                            cmsOptions: z.never().optional(),
                            categoryFieldOptions: z
                              .object({
                                isPrimary: z
                                  .boolean()
                                  .describe(
                                    "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Options for the CATEGORY_FIELD plugin.'
                              ),
                          }),
                        ])
                      )
                    )
                    .max(100)
                    .optional(),
                  condition: z
                    .object({
                      groupName: z
                        .string()
                        .describe(
                          'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                      permission: z
                        .enum(['ANYONE', 'RESTRICTED'])
                        .describe(
                          'If present, the field may be omitted from the results, if the requester lacks sufficient permissions to access data stored in the field.'
                        )
                        .optional(),
                    })
                    .describe(
                      'This indicates that this fields is conditional and is not retrieved by default.'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    numberRange: z.never().optional(),
                    stringLengthRange: z.never().optional(),
                    arraySizeRange: z.never().optional(),
                  }),
                  z.object({
                    stringLengthRange: z.never().optional(),
                    arraySizeRange: z.never().optional(),
                    numberRange: z
                      .object({
                        min: z
                          .number()
                          .describe(
                            'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                          )
                          .optional()
                          .nullable(),
                        max: z
                          .number()
                          .describe(
                            'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Range of possible values for a numerical field.'
                      ),
                  }),
                  z.object({
                    numberRange: z.never().optional(),
                    arraySizeRange: z.never().optional(),
                    stringLengthRange: z
                      .object({
                        minLength: z
                          .number()
                          .int()
                          .describe(
                            'Minimum permitted length for a text field.\n\nDefault: No validation'
                          )
                          .optional()
                          .nullable(),
                        maxLength: z
                          .number()
                          .int()
                          .describe(
                            'Maximum permitted length for a text field.\n\nDefault: No validation'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
                      ),
                  }),
                  z.object({
                    numberRange: z.never().optional(),
                    stringLengthRange: z.never().optional(),
                    arraySizeRange: z
                      .object({
                        minSize: z
                          .number()
                          .int()
                          .describe(
                            'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                          )
                          .optional()
                          .nullable(),
                        maxSize: z
                          .number()
                          .int()
                          .describe(
                            'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
                      ),
                  }),
                ])
              )
            )
            .max(1000)
            .optional(),
          permissions: z
            .object({
              insert: z
                .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
                .describe(
                  'Role required to insert items into the collection.\n\nDefault: `CMS_EDITOR`'
                )
                .optional(),
              update: z
                .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
                .describe(
                  'Role required to update collection items.\n\nDefault: `CMS_EDITOR`'
                )
                .optional(),
              remove: z
                .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
                .describe(
                  'Role required to remove items from the collection.\n\nDefault: `CMS_EDITOR`'
                )
                .optional(),
              read: z
                .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
                .describe(
                  'Role required to read collection items.\n\nDefault: `SITE_MEMBER_AUTHOR`'
                )
                .optional(),
            })
            .describe(
              '[Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest role needed to perform each action.'
            )
            .optional(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              "Collection's current revision number, which increments each time the collection is updated. For an update operation to succeed, you must specify the latest revision number."
            )
            .optional()
            .nullable(),
          plugins: z
            .array(
              z.intersection(
                z.object({
                  type: z
                    .enum([
                      'PUBLISH',
                      'SINGLE_ITEM',
                      'URLIZED',
                      'GRIDAPPLESS',
                      'MULTILINGUAL',
                      'SHARED',
                      'EDITABLE_PAGE_LINK',
                      'CMS',
                    ])
                    .describe('Plugin type.')
                    .optional(),
                }),
                z.xor([
                  z.object({
                    publishOptions: z.never().optional(),
                    singleItemOptions: z.never().optional(),
                    urlizedOptions: z.never().optional(),
                    multilingualOptions: z.never().optional(),
                    editablePageLinkOptions: z.never().optional(),
                    cmsOptions: z.never().optional(),
                  }),
                  z.object({
                    singleItemOptions: z.never().optional(),
                    urlizedOptions: z.never().optional(),
                    multilingualOptions: z.never().optional(),
                    editablePageLinkOptions: z.never().optional(),
                    cmsOptions: z.never().optional(),
                    publishOptions: z
                      .object({
                        defaultStatus: z
                          .enum(['PUBLISHED', 'DRAFT'])
                          .describe('Default status.')
                          .optional(),
                      })
                      .describe(
                        'Options for the Publish plugin.\nThis plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.'
                      ),
                  }),
                  z.object({
                    publishOptions: z.never().optional(),
                    urlizedOptions: z.never().optional(),
                    multilingualOptions: z.never().optional(),
                    editablePageLinkOptions: z.never().optional(),
                    cmsOptions: z.never().optional(),
                    singleItemOptions: z
                      .object({
                        singleItemId: z
                          .string()
                          .describe(
                            'ID of the single item in this collection. If you insert or update an item, its ID value is always changed to this.'
                          )
                          .max(16000)
                          .optional(),
                      })
                      .describe('Options for the Single Item plugin.'),
                  }),
                  z.object({
                    publishOptions: z.never().optional(),
                    singleItemOptions: z.never().optional(),
                    multilingualOptions: z.never().optional(),
                    editablePageLinkOptions: z.never().optional(),
                    cmsOptions: z.never().optional(),
                    urlizedOptions: z
                      .object({
                        format: z
                          .enum(['ORIGINAL', 'PLAIN'])
                          .describe(
                            'Encoding method for generating a URL in ASCII characters.'
                          )
                          .optional(),
                      })
                      .describe('Options for the Urlized plugin.'),
                  }),
                  z.object({
                    publishOptions: z.never().optional(),
                    singleItemOptions: z.never().optional(),
                    urlizedOptions: z.never().optional(),
                    editablePageLinkOptions: z.never().optional(),
                    cmsOptions: z.never().optional(),
                    multilingualOptions: z
                      .object({
                        translatableFieldKeys: z
                          .array(z.string())
                          .max(1000)
                          .optional(),
                      })
                      .describe('Options for the Multilingual plugin.'),
                  }),
                  z.object({
                    publishOptions: z.never().optional(),
                    singleItemOptions: z.never().optional(),
                    urlizedOptions: z.never().optional(),
                    multilingualOptions: z.never().optional(),
                    cmsOptions: z.never().optional(),
                    editablePageLinkOptions: z
                      .object({
                        isPersisted: z.boolean().optional(),
                        isMutable: z.boolean().optional(),
                      })
                      .describe('Options for the PageLink plugin.'),
                  }),
                  z.object({
                    publishOptions: z.never().optional(),
                    singleItemOptions: z.never().optional(),
                    urlizedOptions: z.never().optional(),
                    multilingualOptions: z.never().optional(),
                    editablePageLinkOptions: z.never().optional(),
                    cmsOptions: z
                      .object({
                        siteSort: z
                          .object({
                            sort: z
                              .array(
                                z.object({
                                  fieldKey: z
                                    .string()
                                    .describe('Field to sort by.')
                                    .max(1000)
                                    .optional(),
                                  direction: z
                                    .enum(['ASC', 'DESC'])
                                    .describe(
                                      'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
                                    )
                                    .optional(),
                                })
                              )
                              .max(100)
                              .optional(),
                          })
                          .describe(
                            'CMS sort, applied when a collection is displayed on a site.'
                          )
                          .optional(),
                      })
                      .describe('Options for the CMS plugin.'),
                  }),
                ])
              )
            )
            .max(100)
            .optional(),
          pagingModes: z
            .array(z.enum(['OFFSET', 'CURSOR']))
            .max(2)
            .optional(),
          _createdDate: z
            .date()
            .describe('Date the collection was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date the collection was last updated.')
            .optional()
            .nullable(),
        })
      )
      .optional(),
    pagingMetadata: z
      .object({
        count: z
          .number()
          .int()
          .describe('Number of items returned in the response.')
          .optional()
          .nullable(),
        offset: z
          .number()
          .int()
          .describe('Offset that was requested.')
          .optional()
          .nullable(),
        total: z
          .number()
          .int()
          .describe(
            'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
          )
          .optional()
          .nullable(),
        tooManyToCount: z
          .boolean()
          .describe(
            'Flag that indicates the server failed to calculate the `total` field.'
          )
          .optional()
          .nullable(),
      })
      .describe('Paging information.')
      .optional(),
  });
})();
export const UpdateDataCollectionRequest = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    collection: z
      .object({
        _id: z
          .string()
          .describe(
            '[Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.\nFor [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.'
          )
          .max(256),
        collectionType: z
          .enum(['NATIVE', 'WIX_APP', 'BLOCKS_APP', 'EXTERNAL'])
          .optional(),
        ownerAppId: z
          .string()
          .describe(
            'ID of the app that defined this collection. For collections defined by Wix users, this value is null.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        maxPageSize: z
          .number()
          .int()
          .describe(
            "Maximum number of items returned in a single query, based on the underlying storage.\nNative collections have a maximum page size of 1000 for offset-based queries or 100 for cursor-based queries.\nExternal collections' maximum page size defaults to 50, but an external provider can set any maximum value up to 1000."
          )
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe(
            "Collection's display name as shown in the CMS. For example, `My First Collection`."
          )
          .max(1000)
          .optional()
          .nullable(),
        defaultDisplayOrder: z
          .object({
            fieldKey: z
              .string()
              .describe('Field to sort by.')
              .max(1000)
              .optional(),
            direction: z.enum(['ASC', 'DESC']).optional(),
          })
          .describe(
            "Default item sorting order when a query doesn't specify one."
          )
          .optional(),
        displayNamespace: z
          .string()
          .describe(
            'UI-friendly namespace of the Wix app with which the data collection is associated, such as Stores or Bookings.\nEmpty for all data collections not owned by Wix apps.'
          )
          .max(1000)
          .optional()
          .nullable(),
        displayField: z
          .string()
          .describe(
            'Field whose value the CMS displays to represent the collection item when referenced in a different collection.'
          )
          .max(1000)
          .optional()
          .nullable(),
        capabilities: z
          .object({
            dataOperations: z
              .array(
                z.enum([
                  'AGGREGATE',
                  'BULK_INSERT',
                  'BULK_REMOVE',
                  'BULK_SAVE',
                  'BULK_UPDATE',
                  'COUNT',
                  'DISTINCT',
                  'FIND',
                  'GET',
                  'INSERT',
                  'INSERT_REFERENCE',
                  'IS_REFERENCED',
                  'QUERY_REFERENCED',
                  'REMOVE',
                  'REMOVE_REFERENCE',
                  'REPLACE_REFERENCES',
                  'SAVE',
                  'TRUNCATE',
                  'UPDATE',
                  'PATCH',
                  'BULK_PATCH',
                ])
              )
              .max(100)
              .optional(),
            collectionOperations: z
              .array(z.enum(['UPDATE', 'REMOVE']))
              .max(100)
              .optional(),
            indexLimits: z
              .object({
                regular: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular (non-unique) indexes allowed for this collection.'
                  )
                  .optional(),
                unique: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of unique indexes allowed for this collection.'
                  )
                  .optional(),
                total: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular and unique indexes allowed for this collection.'
                  )
                  .optional(),
              })
              .describe('Maximum number of indexes for the collection.')
              .optional(),
          })
          .describe(
            'Capabilities the collection supports.\nCapabilities are set by default. Values depend on the collection type.'
          )
          .optional(),
        fields: z
          .array(
            z.intersection(
              z.object({
                key: z
                  .string()
                  .describe(
                    'Unique identifier for the field. For example, `firstName`.'
                  )
                  .max(1000),
                displayName: z
                  .string()
                  .describe(
                    "Field's display name when displayed in the CMS. For example, `First Name`."
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                type: z.enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ]),
                typeMetadata:
                  comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                    .describe(
                      'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                    )
                    .optional(),
                systemField: z
                  .boolean()
                  .describe('Whether the field is a system field.')
                  .optional(),
                capabilities: z
                  .object({
                    sortable: z
                      .boolean()
                      .describe(
                        'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryOperators: z
                      .array(
                        z.enum([
                          'EQ',
                          'LT',
                          'GT',
                          'NE',
                          'LTE',
                          'GTE',
                          'STARTS_WITH',
                          'ENDS_WITH',
                          'CONTAINS',
                          'HAS_SOME',
                          'HAS_ALL',
                          'EXISTS',
                          'URLIZED',
                        ])
                      )
                      .max(100)
                      .optional(),
                  })
                  .describe('Capabilities the field supports.')
                  .optional(),
                encrypted: z
                  .boolean()
                  .describe('Whether the field is encrypted.')
                  .optional(),
                description: z
                  .string()
                  .describe('Field description.')
                  .max(16000)
                  .optional()
                  .nullable(),
                readOnly: z
                  .boolean()
                  .describe(
                    "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
                  )
                  .optional()
                  .nullable(),
                immutable: z
                  .boolean()
                  .describe(
                    'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                required: z
                  .boolean()
                  .describe(
                    'Whether the field is required.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                plugins: z
                  .array(
                    z.intersection(
                      z.object({ type: z.enum(['UNKNOWN', 'CMS']).optional() }),
                      z.xor([
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z.never().optional(),
                        }),
                        z.object({
                          categoryFieldOptions: z.never().optional(),
                          cmsOptions: z
                            .object({
                              internal: z
                                .boolean()
                                .describe(
                                  'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                                )
                                .optional(),
                            })
                            .describe('Options for the CMS plugin.'),
                        }),
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z
                            .object({
                              isPrimary: z
                                .boolean()
                                .describe(
                                  "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                                )
                                .optional(),
                            })
                            .describe('Options for the CATEGORY_FIELD plugin.'),
                        }),
                      ])
                    )
                  )
                  .max(100)
                  .optional(),
                condition: z
                  .object({
                    groupName: z
                      .string()
                      .describe(
                        'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    permission: z.enum(['ANYONE', 'RESTRICTED']).optional(),
                  })
                  .describe(
                    'This indicates that this fields is conditional and is not retrieved by default.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                }),
                z.object({
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  numberRange: z
                    .object({
                      min: z
                        .number()
                        .describe(
                          'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      max: z
                        .number()
                        .describe(
                          'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Range of possible values for a numerical field.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  stringLengthRange: z
                    .object({
                      minLength: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxLength: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z
                    .object({
                      minSize: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxSize: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
                    ),
                }),
              ])
            )
          )
          .max(1000)
          .optional(),
        permissions: z
          .object({
            insert: z.enum([
              'ADMIN',
              'SITE_MEMBER_AUTHOR',
              'SITE_MEMBER',
              'ANYONE',
            ]),
            update: z.enum([
              'ADMIN',
              'SITE_MEMBER_AUTHOR',
              'SITE_MEMBER',
              'ANYONE',
            ]),
            remove: z.enum([
              'ADMIN',
              'SITE_MEMBER_AUTHOR',
              'SITE_MEMBER',
              'ANYONE',
            ]),
            read: z.enum([
              'ADMIN',
              'SITE_MEMBER_AUTHOR',
              'SITE_MEMBER',
              'ANYONE',
            ]),
          })
          .describe(
            '[Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest role needed to perform each action.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            "Collection's current revision number, which increments each time the collection is updated. For an update operation to succeed, you must specify the latest revision number."
          ),
        plugins: z
          .array(
            z.intersection(
              z.object({
                type: z
                  .enum([
                    'PUBLISH',
                    'SINGLE_ITEM',
                    'URLIZED',
                    'GRIDAPPLESS',
                    'MULTILINGUAL',
                    'SHARED',
                    'EDITABLE_PAGE_LINK',
                    'CMS',
                  ])
                  .optional(),
              }),
              z.xor([
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                }),
                z.object({
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  publishOptions: z
                    .object({
                      defaultStatus: z
                        .enum(['PUBLISHED', 'DRAFT'])
                        .describe('Default status.')
                        .optional(),
                    })
                    .describe(
                      'Options for the Publish plugin.\nThis plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.'
                    ),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  singleItemOptions: z
                    .object({
                      singleItemId: z
                        .string()
                        .describe(
                          'ID of the single item in this collection. If you insert or update an item, its ID value is always changed to this.'
                        )
                        .max(16000)
                        .optional(),
                    })
                    .describe('Options for the Single Item plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  urlizedOptions: z
                    .object({
                      format: z
                        .enum(['ORIGINAL', 'PLAIN'])
                        .describe(
                          'Encoding method for generating a URL in ASCII characters.'
                        )
                        .optional(),
                    })
                    .describe('Options for the Urlized plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  multilingualOptions: z
                    .object({
                      translatableFieldKeys: z
                        .array(z.string())
                        .max(1000)
                        .optional(),
                    })
                    .describe('Options for the Multilingual plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  editablePageLinkOptions: z
                    .object({
                      isPersisted: z.boolean().optional(),
                      isMutable: z.boolean().optional(),
                    })
                    .describe('Options for the PageLink plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z
                    .object({
                      siteSort: z
                        .object({
                          sort: z
                            .array(
                              z.object({
                                fieldKey: z
                                  .string()
                                  .describe('Field to sort by.')
                                  .max(1000)
                                  .optional(),
                                direction: z.enum(['ASC', 'DESC']).optional(),
                              })
                            )
                            .max(100)
                            .optional(),
                        })
                        .describe(
                          'CMS sort, applied when a collection is displayed on a site.'
                        )
                        .optional(),
                    })
                    .describe('Options for the CMS plugin.'),
                }),
              ])
            )
          )
          .max(100)
          .optional(),
        pagingModes: z
          .array(z.enum(['OFFSET', 'CURSOR']))
          .max(2)
          .optional(),
        _createdDate: z
          .date()
          .describe('Date the collection was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date the collection was last updated.')
          .optional()
          .nullable(),
      })
      .describe(
        'Updated collection details. The existing collection is replaced with this version.'
      ),
  });
})();
export const UpdateDataCollectionResponse = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .describe(
                        'Field type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data).'
                      )
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    _id: z
      .string()
      .describe(
        '[Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.\nFor [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.'
      )
      .max(256)
      .optional(),
    collectionType: z
      .enum(['NATIVE', 'WIX_APP', 'BLOCKS_APP', 'EXTERNAL'])
      .describe(
        'Collection type. Indicates how the collection was created and how it is stored.'
      )
      .optional(),
    ownerAppId: z
      .string()
      .describe(
        'ID of the app that defined this collection. For collections defined by Wix users, this value is null.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    maxPageSize: z
      .number()
      .int()
      .describe(
        "Maximum number of items returned in a single query, based on the underlying storage.\nNative collections have a maximum page size of 1000 for offset-based queries or 100 for cursor-based queries.\nExternal collections' maximum page size defaults to 50, but an external provider can set any maximum value up to 1000."
      )
      .optional()
      .nullable(),
    displayName: z
      .string()
      .describe(
        "Collection's display name as shown in the CMS. For example, `My First Collection`."
      )
      .max(1000)
      .optional()
      .nullable(),
    defaultDisplayOrder: z
      .object({
        fieldKey: z.string().describe('Field to sort by.').max(1000).optional(),
        direction: z
          .enum(['ASC', 'DESC'])
          .describe(
            'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
          )
          .optional(),
      })
      .describe("Default item sorting order when a query doesn't specify one.")
      .optional(),
    displayNamespace: z
      .string()
      .describe(
        'UI-friendly namespace of the Wix app with which the data collection is associated, such as Stores or Bookings.\nEmpty for all data collections not owned by Wix apps.'
      )
      .max(1000)
      .optional()
      .nullable(),
    displayField: z
      .string()
      .describe(
        'Field whose value the CMS displays to represent the collection item when referenced in a different collection.'
      )
      .max(1000)
      .optional()
      .nullable(),
    capabilities: z
      .object({
        dataOperations: z
          .array(
            z.enum([
              'AGGREGATE',
              'BULK_INSERT',
              'BULK_REMOVE',
              'BULK_SAVE',
              'BULK_UPDATE',
              'COUNT',
              'DISTINCT',
              'FIND',
              'GET',
              'INSERT',
              'INSERT_REFERENCE',
              'IS_REFERENCED',
              'QUERY_REFERENCED',
              'REMOVE',
              'REMOVE_REFERENCE',
              'REPLACE_REFERENCES',
              'SAVE',
              'TRUNCATE',
              'UPDATE',
              'PATCH',
              'BULK_PATCH',
            ])
          )
          .max(100)
          .optional(),
        collectionOperations: z
          .array(z.enum(['UPDATE', 'REMOVE']))
          .max(100)
          .optional(),
        indexLimits: z
          .object({
            regular: z
              .number()
              .int()
              .describe(
                'Maximum number of regular (non-unique) indexes allowed for this collection.'
              )
              .optional(),
            unique: z
              .number()
              .int()
              .describe(
                'Maximum number of unique indexes allowed for this collection.'
              )
              .optional(),
            total: z
              .number()
              .int()
              .describe(
                'Maximum number of regular and unique indexes allowed for this collection.'
              )
              .optional(),
          })
          .describe('Maximum number of indexes for the collection.')
          .optional(),
      })
      .describe(
        'Capabilities the collection supports.\nCapabilities are set by default. Values depend on the collection type.'
      )
      .optional(),
    fields: z
      .array(
        z.intersection(
          z.object({
            key: z
              .string()
              .describe(
                'Unique identifier for the field. For example, `firstName`.'
              )
              .max(1000)
              .optional(),
            displayName: z
              .string()
              .describe(
                "Field's display name when displayed in the CMS. For example, `First Name`."
              )
              .max(1000)
              .optional()
              .nullable(),
            type: z
              .enum([
                'TEXT',
                'NUMBER',
                'DATE',
                'DATETIME',
                'IMAGE',
                'BOOLEAN',
                'DOCUMENT',
                'URL',
                'RICH_TEXT',
                'VIDEO',
                'ANY',
                'ARRAY_STRING',
                'ARRAY_DOCUMENT',
                'AUDIO',
                'TIME',
                'LANGUAGE',
                'RICH_CONTENT',
                'MEDIA_GALLERY',
                'ADDRESS',
                'PAGE_LINK',
                'REFERENCE',
                'MULTI_REFERENCE',
                'OBJECT',
                'ARRAY',
                'LEGACY_TIME',
                'LEGACY_BOOK',
                'LEGACY_EXTERNAL_URL',
                'LEGACY_BROKEN_REFERENCE',
                'LEGACY_IMAGE',
                'LEGACY_COLOR',
                'LEGACY_EXTERNAL_VIDEO',
                'EMAIL',
              ])
              .describe(
                "Field's data type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data)."
              )
              .optional(),
            typeMetadata:
              comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            systemField: z
              .boolean()
              .describe('Whether the field is a system field.')
              .optional(),
            capabilities: z
              .object({
                sortable: z
                  .boolean()
                  .describe(
                    'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                  )
                  .optional(),
                queryOperators: z
                  .array(
                    z.enum([
                      'EQ',
                      'LT',
                      'GT',
                      'NE',
                      'LTE',
                      'GTE',
                      'STARTS_WITH',
                      'ENDS_WITH',
                      'CONTAINS',
                      'HAS_SOME',
                      'HAS_ALL',
                      'EXISTS',
                      'URLIZED',
                    ])
                  )
                  .max(100)
                  .optional(),
              })
              .describe('Capabilities the field supports.')
              .optional(),
            encrypted: z
              .boolean()
              .describe('Whether the field is encrypted.')
              .optional(),
            description: z
              .string()
              .describe('Field description.')
              .max(16000)
              .optional()
              .nullable(),
            readOnly: z
              .boolean()
              .describe(
                "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
              )
              .optional()
              .nullable(),
            immutable: z
              .boolean()
              .describe(
                'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
              )
              .optional()
              .nullable(),
            required: z
              .boolean()
              .describe('Whether the field is required.\n\nDefault: `false`')
              .optional()
              .nullable(),
            plugins: z
              .array(
                z.intersection(
                  z.object({ type: z.enum(['UNKNOWN', 'CMS']).optional() }),
                  z.xor([
                    z.object({
                      cmsOptions: z.never().optional(),
                      categoryFieldOptions: z.never().optional(),
                    }),
                    z.object({
                      categoryFieldOptions: z.never().optional(),
                      cmsOptions: z
                        .object({
                          internal: z
                            .boolean()
                            .describe(
                              'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                            )
                            .optional(),
                        })
                        .describe('Options for the CMS plugin.'),
                    }),
                    z.object({
                      cmsOptions: z.never().optional(),
                      categoryFieldOptions: z
                        .object({
                          isPrimary: z
                            .boolean()
                            .describe(
                              "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                            )
                            .optional(),
                        })
                        .describe('Options for the CATEGORY_FIELD plugin.'),
                    }),
                  ])
                )
              )
              .max(100)
              .optional(),
            condition: z
              .object({
                groupName: z
                  .string()
                  .describe(
                    'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                permission: z
                  .enum(['ANYONE', 'RESTRICTED'])
                  .describe(
                    'If present, the field may be omitted from the results, if the requester lacks sufficient permissions to access data stored in the field.'
                  )
                  .optional(),
              })
              .describe(
                'This indicates that this fields is conditional and is not retrieved by default.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              numberRange: z.never().optional(),
              stringLengthRange: z.never().optional(),
              arraySizeRange: z.never().optional(),
            }),
            z.object({
              stringLengthRange: z.never().optional(),
              arraySizeRange: z.never().optional(),
              numberRange: z
                .object({
                  min: z
                    .number()
                    .describe(
                      'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                  max: z
                    .number()
                    .describe(
                      'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Range of possible values for a numerical field.'),
            }),
            z.object({
              numberRange: z.never().optional(),
              arraySizeRange: z.never().optional(),
              stringLengthRange: z
                .object({
                  minLength: z
                    .number()
                    .int()
                    .describe(
                      'Minimum permitted length for a text field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                  maxLength: z
                    .number()
                    .int()
                    .describe(
                      'Maximum permitted length for a text field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
                ),
            }),
            z.object({
              numberRange: z.never().optional(),
              stringLengthRange: z.never().optional(),
              arraySizeRange: z
                .object({
                  minSize: z
                    .number()
                    .int()
                    .describe(
                      'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                  maxSize: z
                    .number()
                    .int()
                    .describe(
                      'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
                ),
            }),
          ])
        )
      )
      .max(1000)
      .optional(),
    permissions: z
      .object({
        insert: z
          .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
          .describe(
            'Role required to insert items into the collection.\n\nDefault: `CMS_EDITOR`'
          )
          .optional(),
        update: z
          .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
          .describe(
            'Role required to update collection items.\n\nDefault: `CMS_EDITOR`'
          )
          .optional(),
        remove: z
          .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
          .describe(
            'Role required to remove items from the collection.\n\nDefault: `CMS_EDITOR`'
          )
          .optional(),
        read: z
          .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
          .describe(
            'Role required to read collection items.\n\nDefault: `SITE_MEMBER_AUTHOR`'
          )
          .optional(),
      })
      .describe(
        '[Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest role needed to perform each action.'
      )
      .optional(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        "Collection's current revision number, which increments each time the collection is updated. For an update operation to succeed, you must specify the latest revision number."
      )
      .optional()
      .nullable(),
    plugins: z
      .array(
        z.intersection(
          z.object({
            type: z
              .enum([
                'PUBLISH',
                'SINGLE_ITEM',
                'URLIZED',
                'GRIDAPPLESS',
                'MULTILINGUAL',
                'SHARED',
                'EDITABLE_PAGE_LINK',
                'CMS',
              ])
              .describe('Plugin type.')
              .optional(),
          }),
          z.xor([
            z.object({
              publishOptions: z.never().optional(),
              singleItemOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
            }),
            z.object({
              singleItemOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
              publishOptions: z
                .object({
                  defaultStatus: z
                    .enum(['PUBLISHED', 'DRAFT'])
                    .describe('Default status.')
                    .optional(),
                })
                .describe(
                  'Options for the Publish plugin.\nThis plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.'
                ),
            }),
            z.object({
              publishOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
              singleItemOptions: z
                .object({
                  singleItemId: z
                    .string()
                    .describe(
                      'ID of the single item in this collection. If you insert or update an item, its ID value is always changed to this.'
                    )
                    .max(16000)
                    .optional(),
                })
                .describe('Options for the Single Item plugin.'),
            }),
            z.object({
              publishOptions: z.never().optional(),
              singleItemOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
              urlizedOptions: z
                .object({
                  format: z
                    .enum(['ORIGINAL', 'PLAIN'])
                    .describe(
                      'Encoding method for generating a URL in ASCII characters.'
                    )
                    .optional(),
                })
                .describe('Options for the Urlized plugin.'),
            }),
            z.object({
              publishOptions: z.never().optional(),
              singleItemOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
              multilingualOptions: z
                .object({
                  translatableFieldKeys: z
                    .array(z.string())
                    .max(1000)
                    .optional(),
                })
                .describe('Options for the Multilingual plugin.'),
            }),
            z.object({
              publishOptions: z.never().optional(),
              singleItemOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              cmsOptions: z.never().optional(),
              editablePageLinkOptions: z
                .object({
                  isPersisted: z.boolean().optional(),
                  isMutable: z.boolean().optional(),
                })
                .describe('Options for the PageLink plugin.'),
            }),
            z.object({
              publishOptions: z.never().optional(),
              singleItemOptions: z.never().optional(),
              urlizedOptions: z.never().optional(),
              multilingualOptions: z.never().optional(),
              editablePageLinkOptions: z.never().optional(),
              cmsOptions: z
                .object({
                  siteSort: z
                    .object({
                      sort: z
                        .array(
                          z.object({
                            fieldKey: z
                              .string()
                              .describe('Field to sort by.')
                              .max(1000)
                              .optional(),
                            direction: z
                              .enum(['ASC', 'DESC'])
                              .describe(
                                'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
                              )
                              .optional(),
                          })
                        )
                        .max(100)
                        .optional(),
                    })
                    .describe(
                      'CMS sort, applied when a collection is displayed on a site.'
                    )
                    .optional(),
                })
                .describe('Options for the CMS plugin.'),
            }),
          ])
        )
      )
      .max(100)
      .optional(),
    pagingModes: z
      .array(z.enum(['OFFSET', 'CURSOR']))
      .max(2)
      .optional(),
    _createdDate: z
      .date()
      .describe('Date the collection was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date the collection was last updated.')
      .optional()
      .nullable(),
  });
})();
export const DeleteDataCollectionRequest = z.object({
  dataCollectionId: z
    .string()
    .describe('ID of the collection to delete.')
    .max(256),
});
export const DeleteDataCollectionResponse = z.object({});
export const PatchDataCollectionRequest = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    _id: z
      .string()
      .describe(
        '[Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.\nFor [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.'
      )
      .max(256),
    dataCollection: z
      .object({
        _id: z
          .string()
          .describe(
            '[Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.\nFor [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.'
          )
          .max(256)
          .optional(),
        collectionType: z
          .enum(['NATIVE', 'WIX_APP', 'BLOCKS_APP', 'EXTERNAL'])
          .optional(),
        ownerAppId: z
          .string()
          .describe(
            'ID of the app that defined this collection. For collections defined by Wix users, this value is null.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        maxPageSize: z
          .number()
          .int()
          .describe(
            "Maximum number of items returned in a single query, based on the underlying storage.\nNative collections have a maximum page size of 1000 for offset-based queries or 100 for cursor-based queries.\nExternal collections' maximum page size defaults to 50, but an external provider can set any maximum value up to 1000."
          )
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe(
            "Collection's display name as shown in the CMS. For example, `My First Collection`."
          )
          .max(1000)
          .optional()
          .nullable(),
        defaultDisplayOrder: z
          .object({
            fieldKey: z
              .string()
              .describe('Field to sort by.')
              .max(1000)
              .optional(),
            direction: z.enum(['ASC', 'DESC']).optional(),
          })
          .describe(
            "Default item sorting order when a query doesn't specify one."
          )
          .optional(),
        displayNamespace: z
          .string()
          .describe(
            'UI-friendly namespace of the Wix app with which the data collection is associated, such as Stores or Bookings.\nEmpty for all data collections not owned by Wix apps.'
          )
          .max(1000)
          .optional()
          .nullable(),
        displayField: z
          .string()
          .describe(
            'Field whose value the CMS displays to represent the collection item when referenced in a different collection.'
          )
          .max(1000)
          .optional()
          .nullable(),
        capabilities: z
          .object({
            dataOperations: z
              .array(
                z.enum([
                  'AGGREGATE',
                  'BULK_INSERT',
                  'BULK_REMOVE',
                  'BULK_SAVE',
                  'BULK_UPDATE',
                  'COUNT',
                  'DISTINCT',
                  'FIND',
                  'GET',
                  'INSERT',
                  'INSERT_REFERENCE',
                  'IS_REFERENCED',
                  'QUERY_REFERENCED',
                  'REMOVE',
                  'REMOVE_REFERENCE',
                  'REPLACE_REFERENCES',
                  'SAVE',
                  'TRUNCATE',
                  'UPDATE',
                  'PATCH',
                  'BULK_PATCH',
                ])
              )
              .max(100)
              .optional(),
            collectionOperations: z
              .array(z.enum(['UPDATE', 'REMOVE']))
              .max(100)
              .optional(),
            indexLimits: z
              .object({
                regular: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular (non-unique) indexes allowed for this collection.'
                  )
                  .optional(),
                unique: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of unique indexes allowed for this collection.'
                  )
                  .optional(),
                total: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular and unique indexes allowed for this collection.'
                  )
                  .optional(),
              })
              .describe('Maximum number of indexes for the collection.')
              .optional(),
          })
          .describe(
            'Capabilities the collection supports.\nCapabilities are set by default. Values depend on the collection type.'
          )
          .optional(),
        fields: z
          .array(
            z.intersection(
              z.object({
                key: z
                  .string()
                  .describe(
                    'Unique identifier for the field. For example, `firstName`.'
                  )
                  .max(1000)
                  .optional(),
                displayName: z
                  .string()
                  .describe(
                    "Field's display name when displayed in the CMS. For example, `First Name`."
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'TEXT',
                    'NUMBER',
                    'DATE',
                    'DATETIME',
                    'IMAGE',
                    'BOOLEAN',
                    'DOCUMENT',
                    'URL',
                    'RICH_TEXT',
                    'VIDEO',
                    'ANY',
                    'ARRAY_STRING',
                    'ARRAY_DOCUMENT',
                    'AUDIO',
                    'TIME',
                    'LANGUAGE',
                    'RICH_CONTENT',
                    'MEDIA_GALLERY',
                    'ADDRESS',
                    'PAGE_LINK',
                    'REFERENCE',
                    'MULTI_REFERENCE',
                    'OBJECT',
                    'ARRAY',
                    'LEGACY_TIME',
                    'LEGACY_BOOK',
                    'LEGACY_EXTERNAL_URL',
                    'LEGACY_BROKEN_REFERENCE',
                    'LEGACY_IMAGE',
                    'LEGACY_COLOR',
                    'LEGACY_EXTERNAL_VIDEO',
                    'EMAIL',
                  ])
                  .optional(),
                typeMetadata:
                  comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                    .describe(
                      'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                    )
                    .optional(),
                systemField: z
                  .boolean()
                  .describe('Whether the field is a system field.')
                  .optional(),
                capabilities: z
                  .object({
                    sortable: z
                      .boolean()
                      .describe(
                        'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryOperators: z
                      .array(
                        z.enum([
                          'EQ',
                          'LT',
                          'GT',
                          'NE',
                          'LTE',
                          'GTE',
                          'STARTS_WITH',
                          'ENDS_WITH',
                          'CONTAINS',
                          'HAS_SOME',
                          'HAS_ALL',
                          'EXISTS',
                          'URLIZED',
                        ])
                      )
                      .max(100)
                      .optional(),
                  })
                  .describe('Capabilities the field supports.')
                  .optional(),
                encrypted: z
                  .boolean()
                  .describe('Whether the field is encrypted.')
                  .optional(),
                description: z
                  .string()
                  .describe('Field description.')
                  .max(16000)
                  .optional()
                  .nullable(),
                readOnly: z
                  .boolean()
                  .describe(
                    "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
                  )
                  .optional()
                  .nullable(),
                immutable: z
                  .boolean()
                  .describe(
                    'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                required: z
                  .boolean()
                  .describe(
                    'Whether the field is required.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                plugins: z
                  .array(
                    z.intersection(
                      z.object({ type: z.enum(['UNKNOWN', 'CMS']).optional() }),
                      z.xor([
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z.never().optional(),
                        }),
                        z.object({
                          categoryFieldOptions: z.never().optional(),
                          cmsOptions: z
                            .object({
                              internal: z
                                .boolean()
                                .describe(
                                  'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                                )
                                .optional(),
                            })
                            .describe('Options for the CMS plugin.'),
                        }),
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z
                            .object({
                              isPrimary: z
                                .boolean()
                                .describe(
                                  "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                                )
                                .optional(),
                            })
                            .describe('Options for the CATEGORY_FIELD plugin.'),
                        }),
                      ])
                    )
                  )
                  .max(100)
                  .optional(),
                condition: z
                  .object({
                    groupName: z
                      .string()
                      .describe(
                        'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    permission: z.enum(['ANYONE', 'RESTRICTED']).optional(),
                  })
                  .describe(
                    'This indicates that this fields is conditional and is not retrieved by default.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                }),
                z.object({
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  numberRange: z
                    .object({
                      min: z
                        .number()
                        .describe(
                          'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      max: z
                        .number()
                        .describe(
                          'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Range of possible values for a numerical field.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  stringLengthRange: z
                    .object({
                      minLength: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxLength: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z
                    .object({
                      minSize: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxSize: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
                    ),
                }),
              ])
            )
          )
          .max(1000)
          .optional(),
        permissions: z
          .object({
            insert: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .optional(),
            update: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .optional(),
            remove: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .optional(),
            read: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .optional(),
          })
          .describe(
            '[Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest role needed to perform each action.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            "Collection's current revision number, which increments each time the collection is updated. For an update operation to succeed, you must specify the latest revision number."
          )
          .optional()
          .nullable(),
        plugins: z
          .array(
            z.intersection(
              z.object({
                type: z
                  .enum([
                    'PUBLISH',
                    'SINGLE_ITEM',
                    'URLIZED',
                    'GRIDAPPLESS',
                    'MULTILINGUAL',
                    'SHARED',
                    'EDITABLE_PAGE_LINK',
                    'CMS',
                  ])
                  .optional(),
              }),
              z.xor([
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                }),
                z.object({
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  publishOptions: z
                    .object({
                      defaultStatus: z
                        .enum(['PUBLISHED', 'DRAFT'])
                        .describe('Default status.')
                        .optional(),
                    })
                    .describe(
                      'Options for the Publish plugin.\nThis plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.'
                    ),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  singleItemOptions: z
                    .object({
                      singleItemId: z
                        .string()
                        .describe(
                          'ID of the single item in this collection. If you insert or update an item, its ID value is always changed to this.'
                        )
                        .max(16000)
                        .optional(),
                    })
                    .describe('Options for the Single Item plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  urlizedOptions: z
                    .object({
                      format: z
                        .enum(['ORIGINAL', 'PLAIN'])
                        .describe(
                          'Encoding method for generating a URL in ASCII characters.'
                        )
                        .optional(),
                    })
                    .describe('Options for the Urlized plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  multilingualOptions: z
                    .object({
                      translatableFieldKeys: z
                        .array(z.string())
                        .max(1000)
                        .optional(),
                    })
                    .describe('Options for the Multilingual plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  editablePageLinkOptions: z
                    .object({
                      isPersisted: z.boolean().optional(),
                      isMutable: z.boolean().optional(),
                    })
                    .describe('Options for the PageLink plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z
                    .object({
                      siteSort: z
                        .object({
                          sort: z
                            .array(
                              z.object({
                                fieldKey: z
                                  .string()
                                  .describe('Field to sort by.')
                                  .max(1000)
                                  .optional(),
                                direction: z.enum(['ASC', 'DESC']).optional(),
                              })
                            )
                            .max(100)
                            .optional(),
                        })
                        .describe(
                          'CMS sort, applied when a collection is displayed on a site.'
                        )
                        .optional(),
                    })
                    .describe('Options for the CMS plugin.'),
                }),
              ])
            )
          )
          .max(100)
          .optional(),
        pagingModes: z
          .array(z.enum(['OFFSET', 'CURSOR']))
          .max(2)
          .optional(),
        _createdDate: z
          .date()
          .describe('Date the collection was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date the collection was last updated.')
          .optional()
          .nullable(),
      })
      .describe('Data collection to update.'),
  });
})();
export const PatchDataCollectionResponse = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .describe(
                        'Field type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data).'
                      )
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    dataCollection: z
      .object({
        _id: z
          .string()
          .describe(
            '[Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.\nFor [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.'
          )
          .max(256)
          .optional(),
        collectionType: z
          .enum(['NATIVE', 'WIX_APP', 'BLOCKS_APP', 'EXTERNAL'])
          .describe(
            'Collection type. Indicates how the collection was created and how it is stored.'
          )
          .optional(),
        ownerAppId: z
          .string()
          .describe(
            'ID of the app that defined this collection. For collections defined by Wix users, this value is null.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        maxPageSize: z
          .number()
          .int()
          .describe(
            "Maximum number of items returned in a single query, based on the underlying storage.\nNative collections have a maximum page size of 1000 for offset-based queries or 100 for cursor-based queries.\nExternal collections' maximum page size defaults to 50, but an external provider can set any maximum value up to 1000."
          )
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe(
            "Collection's display name as shown in the CMS. For example, `My First Collection`."
          )
          .max(1000)
          .optional()
          .nullable(),
        defaultDisplayOrder: z
          .object({
            fieldKey: z
              .string()
              .describe('Field to sort by.')
              .max(1000)
              .optional(),
            direction: z
              .enum(['ASC', 'DESC'])
              .describe(
                'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
              )
              .optional(),
          })
          .describe(
            "Default item sorting order when a query doesn't specify one."
          )
          .optional(),
        displayNamespace: z
          .string()
          .describe(
            'UI-friendly namespace of the Wix app with which the data collection is associated, such as Stores or Bookings.\nEmpty for all data collections not owned by Wix apps.'
          )
          .max(1000)
          .optional()
          .nullable(),
        displayField: z
          .string()
          .describe(
            'Field whose value the CMS displays to represent the collection item when referenced in a different collection.'
          )
          .max(1000)
          .optional()
          .nullable(),
        capabilities: z
          .object({
            dataOperations: z
              .array(
                z.enum([
                  'AGGREGATE',
                  'BULK_INSERT',
                  'BULK_REMOVE',
                  'BULK_SAVE',
                  'BULK_UPDATE',
                  'COUNT',
                  'DISTINCT',
                  'FIND',
                  'GET',
                  'INSERT',
                  'INSERT_REFERENCE',
                  'IS_REFERENCED',
                  'QUERY_REFERENCED',
                  'REMOVE',
                  'REMOVE_REFERENCE',
                  'REPLACE_REFERENCES',
                  'SAVE',
                  'TRUNCATE',
                  'UPDATE',
                  'PATCH',
                  'BULK_PATCH',
                ])
              )
              .max(100)
              .optional(),
            collectionOperations: z
              .array(z.enum(['UPDATE', 'REMOVE']))
              .max(100)
              .optional(),
            indexLimits: z
              .object({
                regular: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular (non-unique) indexes allowed for this collection.'
                  )
                  .optional(),
                unique: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of unique indexes allowed for this collection.'
                  )
                  .optional(),
                total: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular and unique indexes allowed for this collection.'
                  )
                  .optional(),
              })
              .describe('Maximum number of indexes for the collection.')
              .optional(),
          })
          .describe(
            'Capabilities the collection supports.\nCapabilities are set by default. Values depend on the collection type.'
          )
          .optional(),
        fields: z
          .array(
            z.intersection(
              z.object({
                key: z
                  .string()
                  .describe(
                    'Unique identifier for the field. For example, `firstName`.'
                  )
                  .max(1000)
                  .optional(),
                displayName: z
                  .string()
                  .describe(
                    "Field's display name when displayed in the CMS. For example, `First Name`."
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'TEXT',
                    'NUMBER',
                    'DATE',
                    'DATETIME',
                    'IMAGE',
                    'BOOLEAN',
                    'DOCUMENT',
                    'URL',
                    'RICH_TEXT',
                    'VIDEO',
                    'ANY',
                    'ARRAY_STRING',
                    'ARRAY_DOCUMENT',
                    'AUDIO',
                    'TIME',
                    'LANGUAGE',
                    'RICH_CONTENT',
                    'MEDIA_GALLERY',
                    'ADDRESS',
                    'PAGE_LINK',
                    'REFERENCE',
                    'MULTI_REFERENCE',
                    'OBJECT',
                    'ARRAY',
                    'LEGACY_TIME',
                    'LEGACY_BOOK',
                    'LEGACY_EXTERNAL_URL',
                    'LEGACY_BROKEN_REFERENCE',
                    'LEGACY_IMAGE',
                    'LEGACY_COLOR',
                    'LEGACY_EXTERNAL_VIDEO',
                    'EMAIL',
                  ])
                  .describe(
                    "Field's data type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data)."
                  )
                  .optional(),
                typeMetadata:
                  comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                    .describe(
                      'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                    )
                    .optional(),
                systemField: z
                  .boolean()
                  .describe('Whether the field is a system field.')
                  .optional(),
                capabilities: z
                  .object({
                    sortable: z
                      .boolean()
                      .describe(
                        'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryOperators: z
                      .array(
                        z.enum([
                          'EQ',
                          'LT',
                          'GT',
                          'NE',
                          'LTE',
                          'GTE',
                          'STARTS_WITH',
                          'ENDS_WITH',
                          'CONTAINS',
                          'HAS_SOME',
                          'HAS_ALL',
                          'EXISTS',
                          'URLIZED',
                        ])
                      )
                      .max(100)
                      .optional(),
                  })
                  .describe('Capabilities the field supports.')
                  .optional(),
                encrypted: z
                  .boolean()
                  .describe('Whether the field is encrypted.')
                  .optional(),
                description: z
                  .string()
                  .describe('Field description.')
                  .max(16000)
                  .optional()
                  .nullable(),
                readOnly: z
                  .boolean()
                  .describe(
                    "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
                  )
                  .optional()
                  .nullable(),
                immutable: z
                  .boolean()
                  .describe(
                    'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                required: z
                  .boolean()
                  .describe(
                    'Whether the field is required.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                plugins: z
                  .array(
                    z.intersection(
                      z.object({ type: z.enum(['UNKNOWN', 'CMS']).optional() }),
                      z.xor([
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z.never().optional(),
                        }),
                        z.object({
                          categoryFieldOptions: z.never().optional(),
                          cmsOptions: z
                            .object({
                              internal: z
                                .boolean()
                                .describe(
                                  'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                                )
                                .optional(),
                            })
                            .describe('Options for the CMS plugin.'),
                        }),
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z
                            .object({
                              isPrimary: z
                                .boolean()
                                .describe(
                                  "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                                )
                                .optional(),
                            })
                            .describe('Options for the CATEGORY_FIELD plugin.'),
                        }),
                      ])
                    )
                  )
                  .max(100)
                  .optional(),
                condition: z
                  .object({
                    groupName: z
                      .string()
                      .describe(
                        'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    permission: z
                      .enum(['ANYONE', 'RESTRICTED'])
                      .describe(
                        'If present, the field may be omitted from the results, if the requester lacks sufficient permissions to access data stored in the field.'
                      )
                      .optional(),
                  })
                  .describe(
                    'This indicates that this fields is conditional and is not retrieved by default.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                }),
                z.object({
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  numberRange: z
                    .object({
                      min: z
                        .number()
                        .describe(
                          'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      max: z
                        .number()
                        .describe(
                          'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Range of possible values for a numerical field.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  stringLengthRange: z
                    .object({
                      minLength: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxLength: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z
                    .object({
                      minSize: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxSize: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
                    ),
                }),
              ])
            )
          )
          .max(1000)
          .optional(),
        permissions: z
          .object({
            insert: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to insert items into the collection.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            update: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to update collection items.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            remove: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to remove items from the collection.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            read: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to read collection items.\n\nDefault: `SITE_MEMBER_AUTHOR`'
              )
              .optional(),
          })
          .describe(
            '[Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest role needed to perform each action.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            "Collection's current revision number, which increments each time the collection is updated. For an update operation to succeed, you must specify the latest revision number."
          )
          .optional()
          .nullable(),
        plugins: z
          .array(
            z.intersection(
              z.object({
                type: z
                  .enum([
                    'PUBLISH',
                    'SINGLE_ITEM',
                    'URLIZED',
                    'GRIDAPPLESS',
                    'MULTILINGUAL',
                    'SHARED',
                    'EDITABLE_PAGE_LINK',
                    'CMS',
                  ])
                  .describe('Plugin type.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                }),
                z.object({
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  publishOptions: z
                    .object({
                      defaultStatus: z
                        .enum(['PUBLISHED', 'DRAFT'])
                        .describe('Default status.')
                        .optional(),
                    })
                    .describe(
                      'Options for the Publish plugin.\nThis plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.'
                    ),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  singleItemOptions: z
                    .object({
                      singleItemId: z
                        .string()
                        .describe(
                          'ID of the single item in this collection. If you insert or update an item, its ID value is always changed to this.'
                        )
                        .max(16000)
                        .optional(),
                    })
                    .describe('Options for the Single Item plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  urlizedOptions: z
                    .object({
                      format: z
                        .enum(['ORIGINAL', 'PLAIN'])
                        .describe(
                          'Encoding method for generating a URL in ASCII characters.'
                        )
                        .optional(),
                    })
                    .describe('Options for the Urlized plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  multilingualOptions: z
                    .object({
                      translatableFieldKeys: z
                        .array(z.string())
                        .max(1000)
                        .optional(),
                    })
                    .describe('Options for the Multilingual plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  editablePageLinkOptions: z
                    .object({
                      isPersisted: z.boolean().optional(),
                      isMutable: z.boolean().optional(),
                    })
                    .describe('Options for the PageLink plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z
                    .object({
                      siteSort: z
                        .object({
                          sort: z
                            .array(
                              z.object({
                                fieldKey: z
                                  .string()
                                  .describe('Field to sort by.')
                                  .max(1000)
                                  .optional(),
                                direction: z
                                  .enum(['ASC', 'DESC'])
                                  .describe(
                                    'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
                                  )
                                  .optional(),
                              })
                            )
                            .max(100)
                            .optional(),
                        })
                        .describe(
                          'CMS sort, applied when a collection is displayed on a site.'
                        )
                        .optional(),
                    })
                    .describe('Options for the CMS plugin.'),
                }),
              ])
            )
          )
          .max(100)
          .optional(),
        pagingModes: z
          .array(z.enum(['OFFSET', 'CURSOR']))
          .max(2)
          .optional(),
        _createdDate: z
          .date()
          .describe('Date the collection was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date the collection was last updated.')
          .optional()
          .nullable(),
      })
      .describe('Updated data collection.')
      .optional(),
  });
})();
export const CreateDataCollectionFieldRequest = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    dataCollectionId: z
      .string()
      .describe('ID of the data collection to update.')
      .max(256),
    options: z.object({
      field: z
        .intersection(
          z.object({
            key: z
              .string()
              .describe(
                'Unique identifier for the field. For example, `firstName`.'
              )
              .max(1000),
            displayName: z
              .string()
              .describe(
                "Field's display name when displayed in the CMS. For example, `First Name`."
              )
              .max(1000)
              .optional()
              .nullable(),
            type: z.enum([
              'TEXT',
              'NUMBER',
              'DATE',
              'DATETIME',
              'IMAGE',
              'BOOLEAN',
              'DOCUMENT',
              'URL',
              'RICH_TEXT',
              'VIDEO',
              'ANY',
              'ARRAY_STRING',
              'ARRAY_DOCUMENT',
              'AUDIO',
              'TIME',
              'LANGUAGE',
              'RICH_CONTENT',
              'MEDIA_GALLERY',
              'ADDRESS',
              'PAGE_LINK',
              'REFERENCE',
              'MULTI_REFERENCE',
              'OBJECT',
              'ARRAY',
              'LEGACY_TIME',
              'LEGACY_BOOK',
              'LEGACY_EXTERNAL_URL',
              'LEGACY_BROKEN_REFERENCE',
              'LEGACY_IMAGE',
              'LEGACY_COLOR',
              'LEGACY_EXTERNAL_VIDEO',
              'EMAIL',
            ]),
            typeMetadata:
              comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            systemField: z
              .boolean()
              .describe('Whether the field is a system field.')
              .optional(),
            capabilities: z
              .object({
                sortable: z
                  .boolean()
                  .describe(
                    'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                  )
                  .optional(),
                queryOperators: z
                  .array(
                    z.enum([
                      'EQ',
                      'LT',
                      'GT',
                      'NE',
                      'LTE',
                      'GTE',
                      'STARTS_WITH',
                      'ENDS_WITH',
                      'CONTAINS',
                      'HAS_SOME',
                      'HAS_ALL',
                      'EXISTS',
                      'URLIZED',
                    ])
                  )
                  .max(100)
                  .optional(),
              })
              .describe('Capabilities the field supports.')
              .optional(),
            encrypted: z
              .boolean()
              .describe('Whether the field is encrypted.')
              .optional(),
            description: z
              .string()
              .describe('Field description.')
              .max(16000)
              .optional()
              .nullable(),
            readOnly: z
              .boolean()
              .describe(
                "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
              )
              .optional()
              .nullable(),
            immutable: z
              .boolean()
              .describe(
                'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
              )
              .optional()
              .nullable(),
            required: z
              .boolean()
              .describe('Whether the field is required.\n\nDefault: `false`')
              .optional()
              .nullable(),
            plugins: z
              .array(
                z.intersection(
                  z.object({ type: z.enum(['UNKNOWN', 'CMS']).optional() }),
                  z.xor([
                    z.object({
                      cmsOptions: z.never().optional(),
                      categoryFieldOptions: z.never().optional(),
                    }),
                    z.object({
                      categoryFieldOptions: z.never().optional(),
                      cmsOptions: z
                        .object({
                          internal: z
                            .boolean()
                            .describe(
                              'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                            )
                            .optional(),
                        })
                        .describe('Options for the CMS plugin.'),
                    }),
                    z.object({
                      cmsOptions: z.never().optional(),
                      categoryFieldOptions: z
                        .object({
                          isPrimary: z
                            .boolean()
                            .describe(
                              "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                            )
                            .optional(),
                        })
                        .describe('Options for the CATEGORY_FIELD plugin.'),
                    }),
                  ])
                )
              )
              .max(100)
              .optional(),
            condition: z
              .object({
                groupName: z
                  .string()
                  .describe(
                    'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                permission: z.enum(['ANYONE', 'RESTRICTED']).optional(),
              })
              .describe(
                'This indicates that this fields is conditional and is not retrieved by default.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              numberRange: z.never().optional(),
              stringLengthRange: z.never().optional(),
              arraySizeRange: z.never().optional(),
            }),
            z.object({
              stringLengthRange: z.never().optional(),
              arraySizeRange: z.never().optional(),
              numberRange: z
                .object({
                  min: z
                    .number()
                    .describe(
                      'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                  max: z
                    .number()
                    .describe(
                      'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Range of possible values for a numerical field.'),
            }),
            z.object({
              numberRange: z.never().optional(),
              arraySizeRange: z.never().optional(),
              stringLengthRange: z
                .object({
                  minLength: z
                    .number()
                    .int()
                    .describe(
                      'Minimum permitted length for a text field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                  maxLength: z
                    .number()
                    .int()
                    .describe(
                      'Maximum permitted length for a text field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
                ),
            }),
            z.object({
              numberRange: z.never().optional(),
              stringLengthRange: z.never().optional(),
              arraySizeRange: z
                .object({
                  minSize: z
                    .number()
                    .int()
                    .describe(
                      'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                  maxSize: z
                    .number()
                    .int()
                    .describe(
                      'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
                ),
            }),
          ])
        )
        .describe('New field to add.'),
    }),
  });
})();
export const CreateDataCollectionFieldResponse = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .describe(
                        'Field type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data).'
                      )
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    dataCollection: z
      .object({
        _id: z
          .string()
          .describe(
            '[Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.\nFor [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.'
          )
          .max(256)
          .optional(),
        collectionType: z
          .enum(['NATIVE', 'WIX_APP', 'BLOCKS_APP', 'EXTERNAL'])
          .describe(
            'Collection type. Indicates how the collection was created and how it is stored.'
          )
          .optional(),
        ownerAppId: z
          .string()
          .describe(
            'ID of the app that defined this collection. For collections defined by Wix users, this value is null.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        maxPageSize: z
          .number()
          .int()
          .describe(
            "Maximum number of items returned in a single query, based on the underlying storage.\nNative collections have a maximum page size of 1000 for offset-based queries or 100 for cursor-based queries.\nExternal collections' maximum page size defaults to 50, but an external provider can set any maximum value up to 1000."
          )
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe(
            "Collection's display name as shown in the CMS. For example, `My First Collection`."
          )
          .max(1000)
          .optional()
          .nullable(),
        defaultDisplayOrder: z
          .object({
            fieldKey: z
              .string()
              .describe('Field to sort by.')
              .max(1000)
              .optional(),
            direction: z
              .enum(['ASC', 'DESC'])
              .describe(
                'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
              )
              .optional(),
          })
          .describe(
            "Default item sorting order when a query doesn't specify one."
          )
          .optional(),
        displayNamespace: z
          .string()
          .describe(
            'UI-friendly namespace of the Wix app with which the data collection is associated, such as Stores or Bookings.\nEmpty for all data collections not owned by Wix apps.'
          )
          .max(1000)
          .optional()
          .nullable(),
        displayField: z
          .string()
          .describe(
            'Field whose value the CMS displays to represent the collection item when referenced in a different collection.'
          )
          .max(1000)
          .optional()
          .nullable(),
        capabilities: z
          .object({
            dataOperations: z
              .array(
                z.enum([
                  'AGGREGATE',
                  'BULK_INSERT',
                  'BULK_REMOVE',
                  'BULK_SAVE',
                  'BULK_UPDATE',
                  'COUNT',
                  'DISTINCT',
                  'FIND',
                  'GET',
                  'INSERT',
                  'INSERT_REFERENCE',
                  'IS_REFERENCED',
                  'QUERY_REFERENCED',
                  'REMOVE',
                  'REMOVE_REFERENCE',
                  'REPLACE_REFERENCES',
                  'SAVE',
                  'TRUNCATE',
                  'UPDATE',
                  'PATCH',
                  'BULK_PATCH',
                ])
              )
              .max(100)
              .optional(),
            collectionOperations: z
              .array(z.enum(['UPDATE', 'REMOVE']))
              .max(100)
              .optional(),
            indexLimits: z
              .object({
                regular: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular (non-unique) indexes allowed for this collection.'
                  )
                  .optional(),
                unique: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of unique indexes allowed for this collection.'
                  )
                  .optional(),
                total: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular and unique indexes allowed for this collection.'
                  )
                  .optional(),
              })
              .describe('Maximum number of indexes for the collection.')
              .optional(),
          })
          .describe(
            'Capabilities the collection supports.\nCapabilities are set by default. Values depend on the collection type.'
          )
          .optional(),
        fields: z
          .array(
            z.intersection(
              z.object({
                key: z
                  .string()
                  .describe(
                    'Unique identifier for the field. For example, `firstName`.'
                  )
                  .max(1000)
                  .optional(),
                displayName: z
                  .string()
                  .describe(
                    "Field's display name when displayed in the CMS. For example, `First Name`."
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'TEXT',
                    'NUMBER',
                    'DATE',
                    'DATETIME',
                    'IMAGE',
                    'BOOLEAN',
                    'DOCUMENT',
                    'URL',
                    'RICH_TEXT',
                    'VIDEO',
                    'ANY',
                    'ARRAY_STRING',
                    'ARRAY_DOCUMENT',
                    'AUDIO',
                    'TIME',
                    'LANGUAGE',
                    'RICH_CONTENT',
                    'MEDIA_GALLERY',
                    'ADDRESS',
                    'PAGE_LINK',
                    'REFERENCE',
                    'MULTI_REFERENCE',
                    'OBJECT',
                    'ARRAY',
                    'LEGACY_TIME',
                    'LEGACY_BOOK',
                    'LEGACY_EXTERNAL_URL',
                    'LEGACY_BROKEN_REFERENCE',
                    'LEGACY_IMAGE',
                    'LEGACY_COLOR',
                    'LEGACY_EXTERNAL_VIDEO',
                    'EMAIL',
                  ])
                  .describe(
                    "Field's data type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data)."
                  )
                  .optional(),
                typeMetadata:
                  comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                    .describe(
                      'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                    )
                    .optional(),
                systemField: z
                  .boolean()
                  .describe('Whether the field is a system field.')
                  .optional(),
                capabilities: z
                  .object({
                    sortable: z
                      .boolean()
                      .describe(
                        'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryOperators: z
                      .array(
                        z.enum([
                          'EQ',
                          'LT',
                          'GT',
                          'NE',
                          'LTE',
                          'GTE',
                          'STARTS_WITH',
                          'ENDS_WITH',
                          'CONTAINS',
                          'HAS_SOME',
                          'HAS_ALL',
                          'EXISTS',
                          'URLIZED',
                        ])
                      )
                      .max(100)
                      .optional(),
                  })
                  .describe('Capabilities the field supports.')
                  .optional(),
                encrypted: z
                  .boolean()
                  .describe('Whether the field is encrypted.')
                  .optional(),
                description: z
                  .string()
                  .describe('Field description.')
                  .max(16000)
                  .optional()
                  .nullable(),
                readOnly: z
                  .boolean()
                  .describe(
                    "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
                  )
                  .optional()
                  .nullable(),
                immutable: z
                  .boolean()
                  .describe(
                    'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                required: z
                  .boolean()
                  .describe(
                    'Whether the field is required.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                plugins: z
                  .array(
                    z.intersection(
                      z.object({ type: z.enum(['UNKNOWN', 'CMS']).optional() }),
                      z.xor([
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z.never().optional(),
                        }),
                        z.object({
                          categoryFieldOptions: z.never().optional(),
                          cmsOptions: z
                            .object({
                              internal: z
                                .boolean()
                                .describe(
                                  'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                                )
                                .optional(),
                            })
                            .describe('Options for the CMS plugin.'),
                        }),
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z
                            .object({
                              isPrimary: z
                                .boolean()
                                .describe(
                                  "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                                )
                                .optional(),
                            })
                            .describe('Options for the CATEGORY_FIELD plugin.'),
                        }),
                      ])
                    )
                  )
                  .max(100)
                  .optional(),
                condition: z
                  .object({
                    groupName: z
                      .string()
                      .describe(
                        'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    permission: z
                      .enum(['ANYONE', 'RESTRICTED'])
                      .describe(
                        'If present, the field may be omitted from the results, if the requester lacks sufficient permissions to access data stored in the field.'
                      )
                      .optional(),
                  })
                  .describe(
                    'This indicates that this fields is conditional and is not retrieved by default.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                }),
                z.object({
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  numberRange: z
                    .object({
                      min: z
                        .number()
                        .describe(
                          'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      max: z
                        .number()
                        .describe(
                          'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Range of possible values for a numerical field.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  stringLengthRange: z
                    .object({
                      minLength: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxLength: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z
                    .object({
                      minSize: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxSize: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
                    ),
                }),
              ])
            )
          )
          .max(1000)
          .optional(),
        permissions: z
          .object({
            insert: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to insert items into the collection.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            update: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to update collection items.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            remove: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to remove items from the collection.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            read: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to read collection items.\n\nDefault: `SITE_MEMBER_AUTHOR`'
              )
              .optional(),
          })
          .describe(
            '[Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest role needed to perform each action.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            "Collection's current revision number, which increments each time the collection is updated. For an update operation to succeed, you must specify the latest revision number."
          )
          .optional()
          .nullable(),
        plugins: z
          .array(
            z.intersection(
              z.object({
                type: z
                  .enum([
                    'PUBLISH',
                    'SINGLE_ITEM',
                    'URLIZED',
                    'GRIDAPPLESS',
                    'MULTILINGUAL',
                    'SHARED',
                    'EDITABLE_PAGE_LINK',
                    'CMS',
                  ])
                  .describe('Plugin type.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                }),
                z.object({
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  publishOptions: z
                    .object({
                      defaultStatus: z
                        .enum(['PUBLISHED', 'DRAFT'])
                        .describe('Default status.')
                        .optional(),
                    })
                    .describe(
                      'Options for the Publish plugin.\nThis plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.'
                    ),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  singleItemOptions: z
                    .object({
                      singleItemId: z
                        .string()
                        .describe(
                          'ID of the single item in this collection. If you insert or update an item, its ID value is always changed to this.'
                        )
                        .max(16000)
                        .optional(),
                    })
                    .describe('Options for the Single Item plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  urlizedOptions: z
                    .object({
                      format: z
                        .enum(['ORIGINAL', 'PLAIN'])
                        .describe(
                          'Encoding method for generating a URL in ASCII characters.'
                        )
                        .optional(),
                    })
                    .describe('Options for the Urlized plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  multilingualOptions: z
                    .object({
                      translatableFieldKeys: z
                        .array(z.string())
                        .max(1000)
                        .optional(),
                    })
                    .describe('Options for the Multilingual plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  editablePageLinkOptions: z
                    .object({
                      isPersisted: z.boolean().optional(),
                      isMutable: z.boolean().optional(),
                    })
                    .describe('Options for the PageLink plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z
                    .object({
                      siteSort: z
                        .object({
                          sort: z
                            .array(
                              z.object({
                                fieldKey: z
                                  .string()
                                  .describe('Field to sort by.')
                                  .max(1000)
                                  .optional(),
                                direction: z
                                  .enum(['ASC', 'DESC'])
                                  .describe(
                                    'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
                                  )
                                  .optional(),
                              })
                            )
                            .max(100)
                            .optional(),
                        })
                        .describe(
                          'CMS sort, applied when a collection is displayed on a site.'
                        )
                        .optional(),
                    })
                    .describe('Options for the CMS plugin.'),
                }),
              ])
            )
          )
          .max(100)
          .optional(),
        pagingModes: z
          .array(z.enum(['OFFSET', 'CURSOR']))
          .max(2)
          .optional(),
        _createdDate: z
          .date()
          .describe('Date the collection was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date the collection was last updated.')
          .optional()
          .nullable(),
      })
      .describe('Updated data collection.')
      .optional(),
  });
})();
export const UpdateDataCollectionFieldRequest = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    dataCollectionId: z
      .string()
      .describe('ID of the data collection to update.')
      .max(256),
    options: z.object({
      field: z
        .intersection(
          z.object({
            key: z
              .string()
              .describe(
                'Unique identifier for the field. For example, `firstName`.'
              )
              .max(1000),
            displayName: z
              .string()
              .describe(
                "Field's display name when displayed in the CMS. For example, `First Name`."
              )
              .max(1000)
              .optional()
              .nullable(),
            type: z.enum([
              'TEXT',
              'NUMBER',
              'DATE',
              'DATETIME',
              'IMAGE',
              'BOOLEAN',
              'DOCUMENT',
              'URL',
              'RICH_TEXT',
              'VIDEO',
              'ANY',
              'ARRAY_STRING',
              'ARRAY_DOCUMENT',
              'AUDIO',
              'TIME',
              'LANGUAGE',
              'RICH_CONTENT',
              'MEDIA_GALLERY',
              'ADDRESS',
              'PAGE_LINK',
              'REFERENCE',
              'MULTI_REFERENCE',
              'OBJECT',
              'ARRAY',
              'LEGACY_TIME',
              'LEGACY_BOOK',
              'LEGACY_EXTERNAL_URL',
              'LEGACY_BROKEN_REFERENCE',
              'LEGACY_IMAGE',
              'LEGACY_COLOR',
              'LEGACY_EXTERNAL_VIDEO',
              'EMAIL',
            ]),
            typeMetadata:
              comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            systemField: z
              .boolean()
              .describe('Whether the field is a system field.')
              .optional(),
            capabilities: z
              .object({
                sortable: z
                  .boolean()
                  .describe(
                    'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                  )
                  .optional(),
                queryOperators: z
                  .array(
                    z.enum([
                      'EQ',
                      'LT',
                      'GT',
                      'NE',
                      'LTE',
                      'GTE',
                      'STARTS_WITH',
                      'ENDS_WITH',
                      'CONTAINS',
                      'HAS_SOME',
                      'HAS_ALL',
                      'EXISTS',
                      'URLIZED',
                    ])
                  )
                  .max(100)
                  .optional(),
              })
              .describe('Capabilities the field supports.')
              .optional(),
            encrypted: z
              .boolean()
              .describe('Whether the field is encrypted.')
              .optional(),
            description: z
              .string()
              .describe('Field description.')
              .max(16000)
              .optional()
              .nullable(),
            readOnly: z
              .boolean()
              .describe(
                "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
              )
              .optional()
              .nullable(),
            immutable: z
              .boolean()
              .describe(
                'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
              )
              .optional()
              .nullable(),
            required: z
              .boolean()
              .describe('Whether the field is required.\n\nDefault: `false`')
              .optional()
              .nullable(),
            plugins: z
              .array(
                z.intersection(
                  z.object({ type: z.enum(['UNKNOWN', 'CMS']).optional() }),
                  z.xor([
                    z.object({
                      cmsOptions: z.never().optional(),
                      categoryFieldOptions: z.never().optional(),
                    }),
                    z.object({
                      categoryFieldOptions: z.never().optional(),
                      cmsOptions: z
                        .object({
                          internal: z
                            .boolean()
                            .describe(
                              'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                            )
                            .optional(),
                        })
                        .describe('Options for the CMS plugin.'),
                    }),
                    z.object({
                      cmsOptions: z.never().optional(),
                      categoryFieldOptions: z
                        .object({
                          isPrimary: z
                            .boolean()
                            .describe(
                              "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                            )
                            .optional(),
                        })
                        .describe('Options for the CATEGORY_FIELD plugin.'),
                    }),
                  ])
                )
              )
              .max(100)
              .optional(),
            condition: z
              .object({
                groupName: z
                  .string()
                  .describe(
                    'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                permission: z.enum(['ANYONE', 'RESTRICTED']).optional(),
              })
              .describe(
                'This indicates that this fields is conditional and is not retrieved by default.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              numberRange: z.never().optional(),
              stringLengthRange: z.never().optional(),
              arraySizeRange: z.never().optional(),
            }),
            z.object({
              stringLengthRange: z.never().optional(),
              arraySizeRange: z.never().optional(),
              numberRange: z
                .object({
                  min: z
                    .number()
                    .describe(
                      'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                  max: z
                    .number()
                    .describe(
                      'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Range of possible values for a numerical field.'),
            }),
            z.object({
              numberRange: z.never().optional(),
              arraySizeRange: z.never().optional(),
              stringLengthRange: z
                .object({
                  minLength: z
                    .number()
                    .int()
                    .describe(
                      'Minimum permitted length for a text field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                  maxLength: z
                    .number()
                    .int()
                    .describe(
                      'Maximum permitted length for a text field.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
                ),
            }),
            z.object({
              numberRange: z.never().optional(),
              stringLengthRange: z.never().optional(),
              arraySizeRange: z
                .object({
                  minSize: z
                    .number()
                    .int()
                    .describe(
                      'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                  maxSize: z
                    .number()
                    .int()
                    .describe(
                      'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
                ),
            }),
          ])
        )
        .describe('Field to update.'),
    }),
  });
})();
export const UpdateDataCollectionFieldResponse = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .describe(
                        'Field type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data).'
                      )
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    dataCollection: z
      .object({
        _id: z
          .string()
          .describe(
            '[Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.\nFor [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.'
          )
          .max(256)
          .optional(),
        collectionType: z
          .enum(['NATIVE', 'WIX_APP', 'BLOCKS_APP', 'EXTERNAL'])
          .describe(
            'Collection type. Indicates how the collection was created and how it is stored.'
          )
          .optional(),
        ownerAppId: z
          .string()
          .describe(
            'ID of the app that defined this collection. For collections defined by Wix users, this value is null.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        maxPageSize: z
          .number()
          .int()
          .describe(
            "Maximum number of items returned in a single query, based on the underlying storage.\nNative collections have a maximum page size of 1000 for offset-based queries or 100 for cursor-based queries.\nExternal collections' maximum page size defaults to 50, but an external provider can set any maximum value up to 1000."
          )
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe(
            "Collection's display name as shown in the CMS. For example, `My First Collection`."
          )
          .max(1000)
          .optional()
          .nullable(),
        defaultDisplayOrder: z
          .object({
            fieldKey: z
              .string()
              .describe('Field to sort by.')
              .max(1000)
              .optional(),
            direction: z
              .enum(['ASC', 'DESC'])
              .describe(
                'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
              )
              .optional(),
          })
          .describe(
            "Default item sorting order when a query doesn't specify one."
          )
          .optional(),
        displayNamespace: z
          .string()
          .describe(
            'UI-friendly namespace of the Wix app with which the data collection is associated, such as Stores or Bookings.\nEmpty for all data collections not owned by Wix apps.'
          )
          .max(1000)
          .optional()
          .nullable(),
        displayField: z
          .string()
          .describe(
            'Field whose value the CMS displays to represent the collection item when referenced in a different collection.'
          )
          .max(1000)
          .optional()
          .nullable(),
        capabilities: z
          .object({
            dataOperations: z
              .array(
                z.enum([
                  'AGGREGATE',
                  'BULK_INSERT',
                  'BULK_REMOVE',
                  'BULK_SAVE',
                  'BULK_UPDATE',
                  'COUNT',
                  'DISTINCT',
                  'FIND',
                  'GET',
                  'INSERT',
                  'INSERT_REFERENCE',
                  'IS_REFERENCED',
                  'QUERY_REFERENCED',
                  'REMOVE',
                  'REMOVE_REFERENCE',
                  'REPLACE_REFERENCES',
                  'SAVE',
                  'TRUNCATE',
                  'UPDATE',
                  'PATCH',
                  'BULK_PATCH',
                ])
              )
              .max(100)
              .optional(),
            collectionOperations: z
              .array(z.enum(['UPDATE', 'REMOVE']))
              .max(100)
              .optional(),
            indexLimits: z
              .object({
                regular: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular (non-unique) indexes allowed for this collection.'
                  )
                  .optional(),
                unique: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of unique indexes allowed for this collection.'
                  )
                  .optional(),
                total: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular and unique indexes allowed for this collection.'
                  )
                  .optional(),
              })
              .describe('Maximum number of indexes for the collection.')
              .optional(),
          })
          .describe(
            'Capabilities the collection supports.\nCapabilities are set by default. Values depend on the collection type.'
          )
          .optional(),
        fields: z
          .array(
            z.intersection(
              z.object({
                key: z
                  .string()
                  .describe(
                    'Unique identifier for the field. For example, `firstName`.'
                  )
                  .max(1000)
                  .optional(),
                displayName: z
                  .string()
                  .describe(
                    "Field's display name when displayed in the CMS. For example, `First Name`."
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'TEXT',
                    'NUMBER',
                    'DATE',
                    'DATETIME',
                    'IMAGE',
                    'BOOLEAN',
                    'DOCUMENT',
                    'URL',
                    'RICH_TEXT',
                    'VIDEO',
                    'ANY',
                    'ARRAY_STRING',
                    'ARRAY_DOCUMENT',
                    'AUDIO',
                    'TIME',
                    'LANGUAGE',
                    'RICH_CONTENT',
                    'MEDIA_GALLERY',
                    'ADDRESS',
                    'PAGE_LINK',
                    'REFERENCE',
                    'MULTI_REFERENCE',
                    'OBJECT',
                    'ARRAY',
                    'LEGACY_TIME',
                    'LEGACY_BOOK',
                    'LEGACY_EXTERNAL_URL',
                    'LEGACY_BROKEN_REFERENCE',
                    'LEGACY_IMAGE',
                    'LEGACY_COLOR',
                    'LEGACY_EXTERNAL_VIDEO',
                    'EMAIL',
                  ])
                  .describe(
                    "Field's data type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data)."
                  )
                  .optional(),
                typeMetadata:
                  comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                    .describe(
                      'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                    )
                    .optional(),
                systemField: z
                  .boolean()
                  .describe('Whether the field is a system field.')
                  .optional(),
                capabilities: z
                  .object({
                    sortable: z
                      .boolean()
                      .describe(
                        'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryOperators: z
                      .array(
                        z.enum([
                          'EQ',
                          'LT',
                          'GT',
                          'NE',
                          'LTE',
                          'GTE',
                          'STARTS_WITH',
                          'ENDS_WITH',
                          'CONTAINS',
                          'HAS_SOME',
                          'HAS_ALL',
                          'EXISTS',
                          'URLIZED',
                        ])
                      )
                      .max(100)
                      .optional(),
                  })
                  .describe('Capabilities the field supports.')
                  .optional(),
                encrypted: z
                  .boolean()
                  .describe('Whether the field is encrypted.')
                  .optional(),
                description: z
                  .string()
                  .describe('Field description.')
                  .max(16000)
                  .optional()
                  .nullable(),
                readOnly: z
                  .boolean()
                  .describe(
                    "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
                  )
                  .optional()
                  .nullable(),
                immutable: z
                  .boolean()
                  .describe(
                    'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                required: z
                  .boolean()
                  .describe(
                    'Whether the field is required.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                plugins: z
                  .array(
                    z.intersection(
                      z.object({ type: z.enum(['UNKNOWN', 'CMS']).optional() }),
                      z.xor([
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z.never().optional(),
                        }),
                        z.object({
                          categoryFieldOptions: z.never().optional(),
                          cmsOptions: z
                            .object({
                              internal: z
                                .boolean()
                                .describe(
                                  'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                                )
                                .optional(),
                            })
                            .describe('Options for the CMS plugin.'),
                        }),
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z
                            .object({
                              isPrimary: z
                                .boolean()
                                .describe(
                                  "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                                )
                                .optional(),
                            })
                            .describe('Options for the CATEGORY_FIELD plugin.'),
                        }),
                      ])
                    )
                  )
                  .max(100)
                  .optional(),
                condition: z
                  .object({
                    groupName: z
                      .string()
                      .describe(
                        'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    permission: z
                      .enum(['ANYONE', 'RESTRICTED'])
                      .describe(
                        'If present, the field may be omitted from the results, if the requester lacks sufficient permissions to access data stored in the field.'
                      )
                      .optional(),
                  })
                  .describe(
                    'This indicates that this fields is conditional and is not retrieved by default.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                }),
                z.object({
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  numberRange: z
                    .object({
                      min: z
                        .number()
                        .describe(
                          'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      max: z
                        .number()
                        .describe(
                          'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Range of possible values for a numerical field.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  stringLengthRange: z
                    .object({
                      minLength: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxLength: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z
                    .object({
                      minSize: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxSize: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
                    ),
                }),
              ])
            )
          )
          .max(1000)
          .optional(),
        permissions: z
          .object({
            insert: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to insert items into the collection.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            update: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to update collection items.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            remove: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to remove items from the collection.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            read: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to read collection items.\n\nDefault: `SITE_MEMBER_AUTHOR`'
              )
              .optional(),
          })
          .describe(
            '[Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest role needed to perform each action.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            "Collection's current revision number, which increments each time the collection is updated. For an update operation to succeed, you must specify the latest revision number."
          )
          .optional()
          .nullable(),
        plugins: z
          .array(
            z.intersection(
              z.object({
                type: z
                  .enum([
                    'PUBLISH',
                    'SINGLE_ITEM',
                    'URLIZED',
                    'GRIDAPPLESS',
                    'MULTILINGUAL',
                    'SHARED',
                    'EDITABLE_PAGE_LINK',
                    'CMS',
                  ])
                  .describe('Plugin type.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                }),
                z.object({
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  publishOptions: z
                    .object({
                      defaultStatus: z
                        .enum(['PUBLISHED', 'DRAFT'])
                        .describe('Default status.')
                        .optional(),
                    })
                    .describe(
                      'Options for the Publish plugin.\nThis plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.'
                    ),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  singleItemOptions: z
                    .object({
                      singleItemId: z
                        .string()
                        .describe(
                          'ID of the single item in this collection. If you insert or update an item, its ID value is always changed to this.'
                        )
                        .max(16000)
                        .optional(),
                    })
                    .describe('Options for the Single Item plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  urlizedOptions: z
                    .object({
                      format: z
                        .enum(['ORIGINAL', 'PLAIN'])
                        .describe(
                          'Encoding method for generating a URL in ASCII characters.'
                        )
                        .optional(),
                    })
                    .describe('Options for the Urlized plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  multilingualOptions: z
                    .object({
                      translatableFieldKeys: z
                        .array(z.string())
                        .max(1000)
                        .optional(),
                    })
                    .describe('Options for the Multilingual plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  editablePageLinkOptions: z
                    .object({
                      isPersisted: z.boolean().optional(),
                      isMutable: z.boolean().optional(),
                    })
                    .describe('Options for the PageLink plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z
                    .object({
                      siteSort: z
                        .object({
                          sort: z
                            .array(
                              z.object({
                                fieldKey: z
                                  .string()
                                  .describe('Field to sort by.')
                                  .max(1000)
                                  .optional(),
                                direction: z
                                  .enum(['ASC', 'DESC'])
                                  .describe(
                                    'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
                                  )
                                  .optional(),
                              })
                            )
                            .max(100)
                            .optional(),
                        })
                        .describe(
                          'CMS sort, applied when a collection is displayed on a site.'
                        )
                        .optional(),
                    })
                    .describe('Options for the CMS plugin.'),
                }),
              ])
            )
          )
          .max(100)
          .optional(),
        pagingModes: z
          .array(z.enum(['OFFSET', 'CURSOR']))
          .max(2)
          .optional(),
        _createdDate: z
          .date()
          .describe('Date the collection was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date the collection was last updated.')
          .optional()
          .nullable(),
      })
      .describe('Updated data collection.')
      .optional(),
  });
})();
export const DeleteDataCollectionFieldRequest = z.object({
  dataCollectionId: z
    .string()
    .describe('ID of the data collection to update.')
    .max(256),
  options: z.object({
    fieldKey: z.string().describe('ID of the field to delete.').max(1000),
  }),
});
export const DeleteDataCollectionFieldResponse = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .describe(
                        'Field type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data).'
                      )
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    dataCollection: z
      .object({
        _id: z
          .string()
          .describe(
            '[Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.\nFor [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.'
          )
          .max(256)
          .optional(),
        collectionType: z
          .enum(['NATIVE', 'WIX_APP', 'BLOCKS_APP', 'EXTERNAL'])
          .describe(
            'Collection type. Indicates how the collection was created and how it is stored.'
          )
          .optional(),
        ownerAppId: z
          .string()
          .describe(
            'ID of the app that defined this collection. For collections defined by Wix users, this value is null.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        maxPageSize: z
          .number()
          .int()
          .describe(
            "Maximum number of items returned in a single query, based on the underlying storage.\nNative collections have a maximum page size of 1000 for offset-based queries or 100 for cursor-based queries.\nExternal collections' maximum page size defaults to 50, but an external provider can set any maximum value up to 1000."
          )
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe(
            "Collection's display name as shown in the CMS. For example, `My First Collection`."
          )
          .max(1000)
          .optional()
          .nullable(),
        defaultDisplayOrder: z
          .object({
            fieldKey: z
              .string()
              .describe('Field to sort by.')
              .max(1000)
              .optional(),
            direction: z
              .enum(['ASC', 'DESC'])
              .describe(
                'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
              )
              .optional(),
          })
          .describe(
            "Default item sorting order when a query doesn't specify one."
          )
          .optional(),
        displayNamespace: z
          .string()
          .describe(
            'UI-friendly namespace of the Wix app with which the data collection is associated, such as Stores or Bookings.\nEmpty for all data collections not owned by Wix apps.'
          )
          .max(1000)
          .optional()
          .nullable(),
        displayField: z
          .string()
          .describe(
            'Field whose value the CMS displays to represent the collection item when referenced in a different collection.'
          )
          .max(1000)
          .optional()
          .nullable(),
        capabilities: z
          .object({
            dataOperations: z
              .array(
                z.enum([
                  'AGGREGATE',
                  'BULK_INSERT',
                  'BULK_REMOVE',
                  'BULK_SAVE',
                  'BULK_UPDATE',
                  'COUNT',
                  'DISTINCT',
                  'FIND',
                  'GET',
                  'INSERT',
                  'INSERT_REFERENCE',
                  'IS_REFERENCED',
                  'QUERY_REFERENCED',
                  'REMOVE',
                  'REMOVE_REFERENCE',
                  'REPLACE_REFERENCES',
                  'SAVE',
                  'TRUNCATE',
                  'UPDATE',
                  'PATCH',
                  'BULK_PATCH',
                ])
              )
              .max(100)
              .optional(),
            collectionOperations: z
              .array(z.enum(['UPDATE', 'REMOVE']))
              .max(100)
              .optional(),
            indexLimits: z
              .object({
                regular: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular (non-unique) indexes allowed for this collection.'
                  )
                  .optional(),
                unique: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of unique indexes allowed for this collection.'
                  )
                  .optional(),
                total: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular and unique indexes allowed for this collection.'
                  )
                  .optional(),
              })
              .describe('Maximum number of indexes for the collection.')
              .optional(),
          })
          .describe(
            'Capabilities the collection supports.\nCapabilities are set by default. Values depend on the collection type.'
          )
          .optional(),
        fields: z
          .array(
            z.intersection(
              z.object({
                key: z
                  .string()
                  .describe(
                    'Unique identifier for the field. For example, `firstName`.'
                  )
                  .max(1000)
                  .optional(),
                displayName: z
                  .string()
                  .describe(
                    "Field's display name when displayed in the CMS. For example, `First Name`."
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'TEXT',
                    'NUMBER',
                    'DATE',
                    'DATETIME',
                    'IMAGE',
                    'BOOLEAN',
                    'DOCUMENT',
                    'URL',
                    'RICH_TEXT',
                    'VIDEO',
                    'ANY',
                    'ARRAY_STRING',
                    'ARRAY_DOCUMENT',
                    'AUDIO',
                    'TIME',
                    'LANGUAGE',
                    'RICH_CONTENT',
                    'MEDIA_GALLERY',
                    'ADDRESS',
                    'PAGE_LINK',
                    'REFERENCE',
                    'MULTI_REFERENCE',
                    'OBJECT',
                    'ARRAY',
                    'LEGACY_TIME',
                    'LEGACY_BOOK',
                    'LEGACY_EXTERNAL_URL',
                    'LEGACY_BROKEN_REFERENCE',
                    'LEGACY_IMAGE',
                    'LEGACY_COLOR',
                    'LEGACY_EXTERNAL_VIDEO',
                    'EMAIL',
                  ])
                  .describe(
                    "Field's data type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data)."
                  )
                  .optional(),
                typeMetadata:
                  comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                    .describe(
                      'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                    )
                    .optional(),
                systemField: z
                  .boolean()
                  .describe('Whether the field is a system field.')
                  .optional(),
                capabilities: z
                  .object({
                    sortable: z
                      .boolean()
                      .describe(
                        'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryOperators: z
                      .array(
                        z.enum([
                          'EQ',
                          'LT',
                          'GT',
                          'NE',
                          'LTE',
                          'GTE',
                          'STARTS_WITH',
                          'ENDS_WITH',
                          'CONTAINS',
                          'HAS_SOME',
                          'HAS_ALL',
                          'EXISTS',
                          'URLIZED',
                        ])
                      )
                      .max(100)
                      .optional(),
                  })
                  .describe('Capabilities the field supports.')
                  .optional(),
                encrypted: z
                  .boolean()
                  .describe('Whether the field is encrypted.')
                  .optional(),
                description: z
                  .string()
                  .describe('Field description.')
                  .max(16000)
                  .optional()
                  .nullable(),
                readOnly: z
                  .boolean()
                  .describe(
                    "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
                  )
                  .optional()
                  .nullable(),
                immutable: z
                  .boolean()
                  .describe(
                    'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                required: z
                  .boolean()
                  .describe(
                    'Whether the field is required.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                plugins: z
                  .array(
                    z.intersection(
                      z.object({ type: z.enum(['UNKNOWN', 'CMS']).optional() }),
                      z.xor([
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z.never().optional(),
                        }),
                        z.object({
                          categoryFieldOptions: z.never().optional(),
                          cmsOptions: z
                            .object({
                              internal: z
                                .boolean()
                                .describe(
                                  'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                                )
                                .optional(),
                            })
                            .describe('Options for the CMS plugin.'),
                        }),
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z
                            .object({
                              isPrimary: z
                                .boolean()
                                .describe(
                                  "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                                )
                                .optional(),
                            })
                            .describe('Options for the CATEGORY_FIELD plugin.'),
                        }),
                      ])
                    )
                  )
                  .max(100)
                  .optional(),
                condition: z
                  .object({
                    groupName: z
                      .string()
                      .describe(
                        'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    permission: z
                      .enum(['ANYONE', 'RESTRICTED'])
                      .describe(
                        'If present, the field may be omitted from the results, if the requester lacks sufficient permissions to access data stored in the field.'
                      )
                      .optional(),
                  })
                  .describe(
                    'This indicates that this fields is conditional and is not retrieved by default.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                }),
                z.object({
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  numberRange: z
                    .object({
                      min: z
                        .number()
                        .describe(
                          'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      max: z
                        .number()
                        .describe(
                          'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Range of possible values for a numerical field.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  stringLengthRange: z
                    .object({
                      minLength: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxLength: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z
                    .object({
                      minSize: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxSize: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
                    ),
                }),
              ])
            )
          )
          .max(1000)
          .optional(),
        permissions: z
          .object({
            insert: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to insert items into the collection.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            update: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to update collection items.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            remove: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to remove items from the collection.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            read: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to read collection items.\n\nDefault: `SITE_MEMBER_AUTHOR`'
              )
              .optional(),
          })
          .describe(
            '[Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest role needed to perform each action.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            "Collection's current revision number, which increments each time the collection is updated. For an update operation to succeed, you must specify the latest revision number."
          )
          .optional()
          .nullable(),
        plugins: z
          .array(
            z.intersection(
              z.object({
                type: z
                  .enum([
                    'PUBLISH',
                    'SINGLE_ITEM',
                    'URLIZED',
                    'GRIDAPPLESS',
                    'MULTILINGUAL',
                    'SHARED',
                    'EDITABLE_PAGE_LINK',
                    'CMS',
                  ])
                  .describe('Plugin type.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                }),
                z.object({
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  publishOptions: z
                    .object({
                      defaultStatus: z
                        .enum(['PUBLISHED', 'DRAFT'])
                        .describe('Default status.')
                        .optional(),
                    })
                    .describe(
                      'Options for the Publish plugin.\nThis plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.'
                    ),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  singleItemOptions: z
                    .object({
                      singleItemId: z
                        .string()
                        .describe(
                          'ID of the single item in this collection. If you insert or update an item, its ID value is always changed to this.'
                        )
                        .max(16000)
                        .optional(),
                    })
                    .describe('Options for the Single Item plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  urlizedOptions: z
                    .object({
                      format: z
                        .enum(['ORIGINAL', 'PLAIN'])
                        .describe(
                          'Encoding method for generating a URL in ASCII characters.'
                        )
                        .optional(),
                    })
                    .describe('Options for the Urlized plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  multilingualOptions: z
                    .object({
                      translatableFieldKeys: z
                        .array(z.string())
                        .max(1000)
                        .optional(),
                    })
                    .describe('Options for the Multilingual plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  editablePageLinkOptions: z
                    .object({
                      isPersisted: z.boolean().optional(),
                      isMutable: z.boolean().optional(),
                    })
                    .describe('Options for the PageLink plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z
                    .object({
                      siteSort: z
                        .object({
                          sort: z
                            .array(
                              z.object({
                                fieldKey: z
                                  .string()
                                  .describe('Field to sort by.')
                                  .max(1000)
                                  .optional(),
                                direction: z
                                  .enum(['ASC', 'DESC'])
                                  .describe(
                                    'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
                                  )
                                  .optional(),
                              })
                            )
                            .max(100)
                            .optional(),
                        })
                        .describe(
                          'CMS sort, applied when a collection is displayed on a site.'
                        )
                        .optional(),
                    })
                    .describe('Options for the CMS plugin.'),
                }),
              ])
            )
          )
          .max(100)
          .optional(),
        pagingModes: z
          .array(z.enum(['OFFSET', 'CURSOR']))
          .max(2)
          .optional(),
        _createdDate: z
          .date()
          .describe('Date the collection was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date the collection was last updated.')
          .optional()
          .nullable(),
      })
      .describe('Updated data collection.')
      .optional(),
  });
})();
export const AddDataCollectionPluginRequest = z.object({
  dataCollectionId: z
    .string()
    .describe('ID of the data collection to update.')
    .max(256),
  options: z.object({
    plugin: z
      .intersection(
        z.object({
          type: z.enum([
            'PUBLISH',
            'SINGLE_ITEM',
            'URLIZED',
            'GRIDAPPLESS',
            'MULTILINGUAL',
            'SHARED',
            'EDITABLE_PAGE_LINK',
            'CMS',
          ]),
        }),
        z.xor([
          z.object({
            publishOptions: z.never().optional(),
            singleItemOptions: z.never().optional(),
            urlizedOptions: z.never().optional(),
            multilingualOptions: z.never().optional(),
            editablePageLinkOptions: z.never().optional(),
            cmsOptions: z.never().optional(),
          }),
          z.object({
            singleItemOptions: z.never().optional(),
            urlizedOptions: z.never().optional(),
            multilingualOptions: z.never().optional(),
            editablePageLinkOptions: z.never().optional(),
            cmsOptions: z.never().optional(),
            publishOptions: z
              .object({
                defaultStatus: z
                  .enum(['PUBLISHED', 'DRAFT'])
                  .describe('Default status.')
                  .optional(),
              })
              .describe(
                'Options for the Publish plugin.\nThis plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.'
              ),
          }),
          z.object({
            publishOptions: z.never().optional(),
            urlizedOptions: z.never().optional(),
            multilingualOptions: z.never().optional(),
            editablePageLinkOptions: z.never().optional(),
            cmsOptions: z.never().optional(),
            singleItemOptions: z
              .object({
                singleItemId: z
                  .string()
                  .describe(
                    'ID of the single item in this collection. If you insert or update an item, its ID value is always changed to this.'
                  )
                  .max(16000)
                  .optional(),
              })
              .describe('Options for the Single Item plugin.'),
          }),
          z.object({
            publishOptions: z.never().optional(),
            singleItemOptions: z.never().optional(),
            multilingualOptions: z.never().optional(),
            editablePageLinkOptions: z.never().optional(),
            cmsOptions: z.never().optional(),
            urlizedOptions: z
              .object({
                format: z
                  .enum(['ORIGINAL', 'PLAIN'])
                  .describe(
                    'Encoding method for generating a URL in ASCII characters.'
                  )
                  .optional(),
              })
              .describe('Options for the Urlized plugin.'),
          }),
          z.object({
            publishOptions: z.never().optional(),
            singleItemOptions: z.never().optional(),
            urlizedOptions: z.never().optional(),
            editablePageLinkOptions: z.never().optional(),
            cmsOptions: z.never().optional(),
            multilingualOptions: z
              .object({
                translatableFieldKeys: z.array(z.string()).max(1000).optional(),
              })
              .describe('Options for the Multilingual plugin.'),
          }),
          z.object({
            publishOptions: z.never().optional(),
            singleItemOptions: z.never().optional(),
            urlizedOptions: z.never().optional(),
            multilingualOptions: z.never().optional(),
            cmsOptions: z.never().optional(),
            editablePageLinkOptions: z
              .object({
                isPersisted: z.boolean().optional(),
                isMutable: z.boolean().optional(),
              })
              .describe('Options for the PageLink plugin.'),
          }),
          z.object({
            publishOptions: z.never().optional(),
            singleItemOptions: z.never().optional(),
            urlizedOptions: z.never().optional(),
            multilingualOptions: z.never().optional(),
            editablePageLinkOptions: z.never().optional(),
            cmsOptions: z
              .object({
                siteSort: z
                  .object({
                    sort: z
                      .array(
                        z.object({
                          fieldKey: z
                            .string()
                            .describe('Field to sort by.')
                            .max(1000)
                            .optional(),
                          direction: z.enum(['ASC', 'DESC']).optional(),
                        })
                      )
                      .max(100)
                      .optional(),
                  })
                  .describe(
                    'CMS sort, applied when a collection is displayed on a site.'
                  )
                  .optional(),
              })
              .describe('Options for the CMS plugin.'),
          }),
        ])
      )
      .describe(
        'Plugin to add to the collection. Must include the plugin type and any type-specific configuration.\nIf a plugin of the same type already exists in the collection, this operation fails.'
      ),
  }),
});
export const AddDataCollectionPluginResponse = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .describe(
                        'Field type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data).'
                      )
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    dataCollection: z
      .object({
        _id: z
          .string()
          .describe(
            '[Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.\nFor [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.'
          )
          .max(256)
          .optional(),
        collectionType: z
          .enum(['NATIVE', 'WIX_APP', 'BLOCKS_APP', 'EXTERNAL'])
          .describe(
            'Collection type. Indicates how the collection was created and how it is stored.'
          )
          .optional(),
        ownerAppId: z
          .string()
          .describe(
            'ID of the app that defined this collection. For collections defined by Wix users, this value is null.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        maxPageSize: z
          .number()
          .int()
          .describe(
            "Maximum number of items returned in a single query, based on the underlying storage.\nNative collections have a maximum page size of 1000 for offset-based queries or 100 for cursor-based queries.\nExternal collections' maximum page size defaults to 50, but an external provider can set any maximum value up to 1000."
          )
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe(
            "Collection's display name as shown in the CMS. For example, `My First Collection`."
          )
          .max(1000)
          .optional()
          .nullable(),
        defaultDisplayOrder: z
          .object({
            fieldKey: z
              .string()
              .describe('Field to sort by.')
              .max(1000)
              .optional(),
            direction: z
              .enum(['ASC', 'DESC'])
              .describe(
                'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
              )
              .optional(),
          })
          .describe(
            "Default item sorting order when a query doesn't specify one."
          )
          .optional(),
        displayNamespace: z
          .string()
          .describe(
            'UI-friendly namespace of the Wix app with which the data collection is associated, such as Stores or Bookings.\nEmpty for all data collections not owned by Wix apps.'
          )
          .max(1000)
          .optional()
          .nullable(),
        displayField: z
          .string()
          .describe(
            'Field whose value the CMS displays to represent the collection item when referenced in a different collection.'
          )
          .max(1000)
          .optional()
          .nullable(),
        capabilities: z
          .object({
            dataOperations: z
              .array(
                z.enum([
                  'AGGREGATE',
                  'BULK_INSERT',
                  'BULK_REMOVE',
                  'BULK_SAVE',
                  'BULK_UPDATE',
                  'COUNT',
                  'DISTINCT',
                  'FIND',
                  'GET',
                  'INSERT',
                  'INSERT_REFERENCE',
                  'IS_REFERENCED',
                  'QUERY_REFERENCED',
                  'REMOVE',
                  'REMOVE_REFERENCE',
                  'REPLACE_REFERENCES',
                  'SAVE',
                  'TRUNCATE',
                  'UPDATE',
                  'PATCH',
                  'BULK_PATCH',
                ])
              )
              .max(100)
              .optional(),
            collectionOperations: z
              .array(z.enum(['UPDATE', 'REMOVE']))
              .max(100)
              .optional(),
            indexLimits: z
              .object({
                regular: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular (non-unique) indexes allowed for this collection.'
                  )
                  .optional(),
                unique: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of unique indexes allowed for this collection.'
                  )
                  .optional(),
                total: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular and unique indexes allowed for this collection.'
                  )
                  .optional(),
              })
              .describe('Maximum number of indexes for the collection.')
              .optional(),
          })
          .describe(
            'Capabilities the collection supports.\nCapabilities are set by default. Values depend on the collection type.'
          )
          .optional(),
        fields: z
          .array(
            z.intersection(
              z.object({
                key: z
                  .string()
                  .describe(
                    'Unique identifier for the field. For example, `firstName`.'
                  )
                  .max(1000)
                  .optional(),
                displayName: z
                  .string()
                  .describe(
                    "Field's display name when displayed in the CMS. For example, `First Name`."
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'TEXT',
                    'NUMBER',
                    'DATE',
                    'DATETIME',
                    'IMAGE',
                    'BOOLEAN',
                    'DOCUMENT',
                    'URL',
                    'RICH_TEXT',
                    'VIDEO',
                    'ANY',
                    'ARRAY_STRING',
                    'ARRAY_DOCUMENT',
                    'AUDIO',
                    'TIME',
                    'LANGUAGE',
                    'RICH_CONTENT',
                    'MEDIA_GALLERY',
                    'ADDRESS',
                    'PAGE_LINK',
                    'REFERENCE',
                    'MULTI_REFERENCE',
                    'OBJECT',
                    'ARRAY',
                    'LEGACY_TIME',
                    'LEGACY_BOOK',
                    'LEGACY_EXTERNAL_URL',
                    'LEGACY_BROKEN_REFERENCE',
                    'LEGACY_IMAGE',
                    'LEGACY_COLOR',
                    'LEGACY_EXTERNAL_VIDEO',
                    'EMAIL',
                  ])
                  .describe(
                    "Field's data type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data)."
                  )
                  .optional(),
                typeMetadata:
                  comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                    .describe(
                      'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                    )
                    .optional(),
                systemField: z
                  .boolean()
                  .describe('Whether the field is a system field.')
                  .optional(),
                capabilities: z
                  .object({
                    sortable: z
                      .boolean()
                      .describe(
                        'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryOperators: z
                      .array(
                        z.enum([
                          'EQ',
                          'LT',
                          'GT',
                          'NE',
                          'LTE',
                          'GTE',
                          'STARTS_WITH',
                          'ENDS_WITH',
                          'CONTAINS',
                          'HAS_SOME',
                          'HAS_ALL',
                          'EXISTS',
                          'URLIZED',
                        ])
                      )
                      .max(100)
                      .optional(),
                  })
                  .describe('Capabilities the field supports.')
                  .optional(),
                encrypted: z
                  .boolean()
                  .describe('Whether the field is encrypted.')
                  .optional(),
                description: z
                  .string()
                  .describe('Field description.')
                  .max(16000)
                  .optional()
                  .nullable(),
                readOnly: z
                  .boolean()
                  .describe(
                    "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
                  )
                  .optional()
                  .nullable(),
                immutable: z
                  .boolean()
                  .describe(
                    'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                required: z
                  .boolean()
                  .describe(
                    'Whether the field is required.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                plugins: z
                  .array(
                    z.intersection(
                      z.object({ type: z.enum(['UNKNOWN', 'CMS']).optional() }),
                      z.xor([
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z.never().optional(),
                        }),
                        z.object({
                          categoryFieldOptions: z.never().optional(),
                          cmsOptions: z
                            .object({
                              internal: z
                                .boolean()
                                .describe(
                                  'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                                )
                                .optional(),
                            })
                            .describe('Options for the CMS plugin.'),
                        }),
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z
                            .object({
                              isPrimary: z
                                .boolean()
                                .describe(
                                  "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                                )
                                .optional(),
                            })
                            .describe('Options for the CATEGORY_FIELD plugin.'),
                        }),
                      ])
                    )
                  )
                  .max(100)
                  .optional(),
                condition: z
                  .object({
                    groupName: z
                      .string()
                      .describe(
                        'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    permission: z
                      .enum(['ANYONE', 'RESTRICTED'])
                      .describe(
                        'If present, the field may be omitted from the results, if the requester lacks sufficient permissions to access data stored in the field.'
                      )
                      .optional(),
                  })
                  .describe(
                    'This indicates that this fields is conditional and is not retrieved by default.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                }),
                z.object({
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  numberRange: z
                    .object({
                      min: z
                        .number()
                        .describe(
                          'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      max: z
                        .number()
                        .describe(
                          'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Range of possible values for a numerical field.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  stringLengthRange: z
                    .object({
                      minLength: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxLength: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z
                    .object({
                      minSize: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxSize: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
                    ),
                }),
              ])
            )
          )
          .max(1000)
          .optional(),
        permissions: z
          .object({
            insert: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to insert items into the collection.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            update: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to update collection items.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            remove: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to remove items from the collection.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            read: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to read collection items.\n\nDefault: `SITE_MEMBER_AUTHOR`'
              )
              .optional(),
          })
          .describe(
            '[Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest role needed to perform each action.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            "Collection's current revision number, which increments each time the collection is updated. For an update operation to succeed, you must specify the latest revision number."
          )
          .optional()
          .nullable(),
        plugins: z
          .array(
            z.intersection(
              z.object({
                type: z
                  .enum([
                    'PUBLISH',
                    'SINGLE_ITEM',
                    'URLIZED',
                    'GRIDAPPLESS',
                    'MULTILINGUAL',
                    'SHARED',
                    'EDITABLE_PAGE_LINK',
                    'CMS',
                  ])
                  .describe('Plugin type.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                }),
                z.object({
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  publishOptions: z
                    .object({
                      defaultStatus: z
                        .enum(['PUBLISHED', 'DRAFT'])
                        .describe('Default status.')
                        .optional(),
                    })
                    .describe(
                      'Options for the Publish plugin.\nThis plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.'
                    ),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  singleItemOptions: z
                    .object({
                      singleItemId: z
                        .string()
                        .describe(
                          'ID of the single item in this collection. If you insert or update an item, its ID value is always changed to this.'
                        )
                        .max(16000)
                        .optional(),
                    })
                    .describe('Options for the Single Item plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  urlizedOptions: z
                    .object({
                      format: z
                        .enum(['ORIGINAL', 'PLAIN'])
                        .describe(
                          'Encoding method for generating a URL in ASCII characters.'
                        )
                        .optional(),
                    })
                    .describe('Options for the Urlized plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  multilingualOptions: z
                    .object({
                      translatableFieldKeys: z
                        .array(z.string())
                        .max(1000)
                        .optional(),
                    })
                    .describe('Options for the Multilingual plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  editablePageLinkOptions: z
                    .object({
                      isPersisted: z.boolean().optional(),
                      isMutable: z.boolean().optional(),
                    })
                    .describe('Options for the PageLink plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z
                    .object({
                      siteSort: z
                        .object({
                          sort: z
                            .array(
                              z.object({
                                fieldKey: z
                                  .string()
                                  .describe('Field to sort by.')
                                  .max(1000)
                                  .optional(),
                                direction: z
                                  .enum(['ASC', 'DESC'])
                                  .describe(
                                    'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
                                  )
                                  .optional(),
                              })
                            )
                            .max(100)
                            .optional(),
                        })
                        .describe(
                          'CMS sort, applied when a collection is displayed on a site.'
                        )
                        .optional(),
                    })
                    .describe('Options for the CMS plugin.'),
                }),
              ])
            )
          )
          .max(100)
          .optional(),
        pagingModes: z
          .array(z.enum(['OFFSET', 'CURSOR']))
          .max(2)
          .optional(),
        _createdDate: z
          .date()
          .describe('Date the collection was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date the collection was last updated.')
          .optional()
          .nullable(),
      })
      .describe('Updated data collection.')
      .optional(),
  });
})();
export const DeleteDataCollectionPluginRequest = z.object({
  dataCollectionId: z
    .string()
    .describe('ID of the data collection to update.')
    .max(256),
  options: z.object({
    pluginType: z.enum([
      'PUBLISH',
      'SINGLE_ITEM',
      'URLIZED',
      'GRIDAPPLESS',
      'MULTILINGUAL',
      'SHARED',
      'EDITABLE_PAGE_LINK',
      'CMS',
    ]),
  }),
});
export const DeleteDataCollectionPluginResponse = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .describe(
                        'Field type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data).'
                      )
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    dataCollection: z
      .object({
        _id: z
          .string()
          .describe(
            '[Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.\nFor [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.'
          )
          .max(256)
          .optional(),
        collectionType: z
          .enum(['NATIVE', 'WIX_APP', 'BLOCKS_APP', 'EXTERNAL'])
          .describe(
            'Collection type. Indicates how the collection was created and how it is stored.'
          )
          .optional(),
        ownerAppId: z
          .string()
          .describe(
            'ID of the app that defined this collection. For collections defined by Wix users, this value is null.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        maxPageSize: z
          .number()
          .int()
          .describe(
            "Maximum number of items returned in a single query, based on the underlying storage.\nNative collections have a maximum page size of 1000 for offset-based queries or 100 for cursor-based queries.\nExternal collections' maximum page size defaults to 50, but an external provider can set any maximum value up to 1000."
          )
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe(
            "Collection's display name as shown in the CMS. For example, `My First Collection`."
          )
          .max(1000)
          .optional()
          .nullable(),
        defaultDisplayOrder: z
          .object({
            fieldKey: z
              .string()
              .describe('Field to sort by.')
              .max(1000)
              .optional(),
            direction: z
              .enum(['ASC', 'DESC'])
              .describe(
                'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
              )
              .optional(),
          })
          .describe(
            "Default item sorting order when a query doesn't specify one."
          )
          .optional(),
        displayNamespace: z
          .string()
          .describe(
            'UI-friendly namespace of the Wix app with which the data collection is associated, such as Stores or Bookings.\nEmpty for all data collections not owned by Wix apps.'
          )
          .max(1000)
          .optional()
          .nullable(),
        displayField: z
          .string()
          .describe(
            'Field whose value the CMS displays to represent the collection item when referenced in a different collection.'
          )
          .max(1000)
          .optional()
          .nullable(),
        capabilities: z
          .object({
            dataOperations: z
              .array(
                z.enum([
                  'AGGREGATE',
                  'BULK_INSERT',
                  'BULK_REMOVE',
                  'BULK_SAVE',
                  'BULK_UPDATE',
                  'COUNT',
                  'DISTINCT',
                  'FIND',
                  'GET',
                  'INSERT',
                  'INSERT_REFERENCE',
                  'IS_REFERENCED',
                  'QUERY_REFERENCED',
                  'REMOVE',
                  'REMOVE_REFERENCE',
                  'REPLACE_REFERENCES',
                  'SAVE',
                  'TRUNCATE',
                  'UPDATE',
                  'PATCH',
                  'BULK_PATCH',
                ])
              )
              .max(100)
              .optional(),
            collectionOperations: z
              .array(z.enum(['UPDATE', 'REMOVE']))
              .max(100)
              .optional(),
            indexLimits: z
              .object({
                regular: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular (non-unique) indexes allowed for this collection.'
                  )
                  .optional(),
                unique: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of unique indexes allowed for this collection.'
                  )
                  .optional(),
                total: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular and unique indexes allowed for this collection.'
                  )
                  .optional(),
              })
              .describe('Maximum number of indexes for the collection.')
              .optional(),
          })
          .describe(
            'Capabilities the collection supports.\nCapabilities are set by default. Values depend on the collection type.'
          )
          .optional(),
        fields: z
          .array(
            z.intersection(
              z.object({
                key: z
                  .string()
                  .describe(
                    'Unique identifier for the field. For example, `firstName`.'
                  )
                  .max(1000)
                  .optional(),
                displayName: z
                  .string()
                  .describe(
                    "Field's display name when displayed in the CMS. For example, `First Name`."
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'TEXT',
                    'NUMBER',
                    'DATE',
                    'DATETIME',
                    'IMAGE',
                    'BOOLEAN',
                    'DOCUMENT',
                    'URL',
                    'RICH_TEXT',
                    'VIDEO',
                    'ANY',
                    'ARRAY_STRING',
                    'ARRAY_DOCUMENT',
                    'AUDIO',
                    'TIME',
                    'LANGUAGE',
                    'RICH_CONTENT',
                    'MEDIA_GALLERY',
                    'ADDRESS',
                    'PAGE_LINK',
                    'REFERENCE',
                    'MULTI_REFERENCE',
                    'OBJECT',
                    'ARRAY',
                    'LEGACY_TIME',
                    'LEGACY_BOOK',
                    'LEGACY_EXTERNAL_URL',
                    'LEGACY_BROKEN_REFERENCE',
                    'LEGACY_IMAGE',
                    'LEGACY_COLOR',
                    'LEGACY_EXTERNAL_VIDEO',
                    'EMAIL',
                  ])
                  .describe(
                    "Field's data type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data)."
                  )
                  .optional(),
                typeMetadata:
                  comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                    .describe(
                      'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                    )
                    .optional(),
                systemField: z
                  .boolean()
                  .describe('Whether the field is a system field.')
                  .optional(),
                capabilities: z
                  .object({
                    sortable: z
                      .boolean()
                      .describe(
                        'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryOperators: z
                      .array(
                        z.enum([
                          'EQ',
                          'LT',
                          'GT',
                          'NE',
                          'LTE',
                          'GTE',
                          'STARTS_WITH',
                          'ENDS_WITH',
                          'CONTAINS',
                          'HAS_SOME',
                          'HAS_ALL',
                          'EXISTS',
                          'URLIZED',
                        ])
                      )
                      .max(100)
                      .optional(),
                  })
                  .describe('Capabilities the field supports.')
                  .optional(),
                encrypted: z
                  .boolean()
                  .describe('Whether the field is encrypted.')
                  .optional(),
                description: z
                  .string()
                  .describe('Field description.')
                  .max(16000)
                  .optional()
                  .nullable(),
                readOnly: z
                  .boolean()
                  .describe(
                    "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
                  )
                  .optional()
                  .nullable(),
                immutable: z
                  .boolean()
                  .describe(
                    'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                required: z
                  .boolean()
                  .describe(
                    'Whether the field is required.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                plugins: z
                  .array(
                    z.intersection(
                      z.object({ type: z.enum(['UNKNOWN', 'CMS']).optional() }),
                      z.xor([
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z.never().optional(),
                        }),
                        z.object({
                          categoryFieldOptions: z.never().optional(),
                          cmsOptions: z
                            .object({
                              internal: z
                                .boolean()
                                .describe(
                                  'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                                )
                                .optional(),
                            })
                            .describe('Options for the CMS plugin.'),
                        }),
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z
                            .object({
                              isPrimary: z
                                .boolean()
                                .describe(
                                  "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                                )
                                .optional(),
                            })
                            .describe('Options for the CATEGORY_FIELD plugin.'),
                        }),
                      ])
                    )
                  )
                  .max(100)
                  .optional(),
                condition: z
                  .object({
                    groupName: z
                      .string()
                      .describe(
                        'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    permission: z
                      .enum(['ANYONE', 'RESTRICTED'])
                      .describe(
                        'If present, the field may be omitted from the results, if the requester lacks sufficient permissions to access data stored in the field.'
                      )
                      .optional(),
                  })
                  .describe(
                    'This indicates that this fields is conditional and is not retrieved by default.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                }),
                z.object({
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  numberRange: z
                    .object({
                      min: z
                        .number()
                        .describe(
                          'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      max: z
                        .number()
                        .describe(
                          'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Range of possible values for a numerical field.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  stringLengthRange: z
                    .object({
                      minLength: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxLength: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z
                    .object({
                      minSize: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxSize: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
                    ),
                }),
              ])
            )
          )
          .max(1000)
          .optional(),
        permissions: z
          .object({
            insert: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to insert items into the collection.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            update: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to update collection items.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            remove: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to remove items from the collection.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            read: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to read collection items.\n\nDefault: `SITE_MEMBER_AUTHOR`'
              )
              .optional(),
          })
          .describe(
            '[Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest role needed to perform each action.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            "Collection's current revision number, which increments each time the collection is updated. For an update operation to succeed, you must specify the latest revision number."
          )
          .optional()
          .nullable(),
        plugins: z
          .array(
            z.intersection(
              z.object({
                type: z
                  .enum([
                    'PUBLISH',
                    'SINGLE_ITEM',
                    'URLIZED',
                    'GRIDAPPLESS',
                    'MULTILINGUAL',
                    'SHARED',
                    'EDITABLE_PAGE_LINK',
                    'CMS',
                  ])
                  .describe('Plugin type.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                }),
                z.object({
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  publishOptions: z
                    .object({
                      defaultStatus: z
                        .enum(['PUBLISHED', 'DRAFT'])
                        .describe('Default status.')
                        .optional(),
                    })
                    .describe(
                      'Options for the Publish plugin.\nThis plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.'
                    ),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  singleItemOptions: z
                    .object({
                      singleItemId: z
                        .string()
                        .describe(
                          'ID of the single item in this collection. If you insert or update an item, its ID value is always changed to this.'
                        )
                        .max(16000)
                        .optional(),
                    })
                    .describe('Options for the Single Item plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  urlizedOptions: z
                    .object({
                      format: z
                        .enum(['ORIGINAL', 'PLAIN'])
                        .describe(
                          'Encoding method for generating a URL in ASCII characters.'
                        )
                        .optional(),
                    })
                    .describe('Options for the Urlized plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  multilingualOptions: z
                    .object({
                      translatableFieldKeys: z
                        .array(z.string())
                        .max(1000)
                        .optional(),
                    })
                    .describe('Options for the Multilingual plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  editablePageLinkOptions: z
                    .object({
                      isPersisted: z.boolean().optional(),
                      isMutable: z.boolean().optional(),
                    })
                    .describe('Options for the PageLink plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z
                    .object({
                      siteSort: z
                        .object({
                          sort: z
                            .array(
                              z.object({
                                fieldKey: z
                                  .string()
                                  .describe('Field to sort by.')
                                  .max(1000)
                                  .optional(),
                                direction: z
                                  .enum(['ASC', 'DESC'])
                                  .describe(
                                    'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
                                  )
                                  .optional(),
                              })
                            )
                            .max(100)
                            .optional(),
                        })
                        .describe(
                          'CMS sort, applied when a collection is displayed on a site.'
                        )
                        .optional(),
                    })
                    .describe('Options for the CMS plugin.'),
                }),
              ])
            )
          )
          .max(100)
          .optional(),
        pagingModes: z
          .array(z.enum(['OFFSET', 'CURSOR']))
          .max(2)
          .optional(),
        _createdDate: z
          .date()
          .describe('Date the collection was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date the collection was last updated.')
          .optional()
          .nullable(),
      })
      .describe('Updated data collection.')
      .optional(),
  });
})();
export const PatchDataCollectionFieldRequest = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    dataCollectionId: z
      .string()
      .describe('ID of the data collection to update.')
      .max(256),
    field: z
      .intersection(
        z.object({
          key: z
            .string()
            .describe(
              'Unique identifier for the field. For example, `firstName`.'
            )
            .max(1000)
            .optional(),
          displayName: z
            .string()
            .describe(
              "Field's display name when displayed in the CMS. For example, `First Name`."
            )
            .max(1000)
            .optional()
            .nullable(),
          type: z
            .enum([
              'TEXT',
              'NUMBER',
              'DATE',
              'DATETIME',
              'IMAGE',
              'BOOLEAN',
              'DOCUMENT',
              'URL',
              'RICH_TEXT',
              'VIDEO',
              'ANY',
              'ARRAY_STRING',
              'ARRAY_DOCUMENT',
              'AUDIO',
              'TIME',
              'LANGUAGE',
              'RICH_CONTENT',
              'MEDIA_GALLERY',
              'ADDRESS',
              'PAGE_LINK',
              'REFERENCE',
              'MULTI_REFERENCE',
              'OBJECT',
              'ARRAY',
              'LEGACY_TIME',
              'LEGACY_BOOK',
              'LEGACY_EXTERNAL_URL',
              'LEGACY_BROKEN_REFERENCE',
              'LEGACY_IMAGE',
              'LEGACY_COLOR',
              'LEGACY_EXTERNAL_VIDEO',
              'EMAIL',
            ])
            .optional(),
          typeMetadata:
            comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
              .describe(
                'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
              )
              .optional(),
          systemField: z
            .boolean()
            .describe('Whether the field is a system field.')
            .optional(),
          capabilities: z
            .object({
              sortable: z
                .boolean()
                .describe(
                  'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                )
                .optional(),
              queryOperators: z
                .array(
                  z.enum([
                    'EQ',
                    'LT',
                    'GT',
                    'NE',
                    'LTE',
                    'GTE',
                    'STARTS_WITH',
                    'ENDS_WITH',
                    'CONTAINS',
                    'HAS_SOME',
                    'HAS_ALL',
                    'EXISTS',
                    'URLIZED',
                  ])
                )
                .max(100)
                .optional(),
            })
            .describe('Capabilities the field supports.')
            .optional(),
          encrypted: z
            .boolean()
            .describe('Whether the field is encrypted.')
            .optional(),
          description: z
            .string()
            .describe('Field description.')
            .max(16000)
            .optional()
            .nullable(),
          readOnly: z
            .boolean()
            .describe(
              "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
            )
            .optional()
            .nullable(),
          immutable: z
            .boolean()
            .describe(
              'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          required: z
            .boolean()
            .describe('Whether the field is required.\n\nDefault: `false`')
            .optional()
            .nullable(),
          plugins: z
            .array(
              z.intersection(
                z.object({ type: z.enum(['UNKNOWN', 'CMS']).optional() }),
                z.xor([
                  z.object({
                    cmsOptions: z.never().optional(),
                    categoryFieldOptions: z.never().optional(),
                  }),
                  z.object({
                    categoryFieldOptions: z.never().optional(),
                    cmsOptions: z
                      .object({
                        internal: z
                          .boolean()
                          .describe(
                            'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                          )
                          .optional(),
                      })
                      .describe('Options for the CMS plugin.'),
                  }),
                  z.object({
                    cmsOptions: z.never().optional(),
                    categoryFieldOptions: z
                      .object({
                        isPrimary: z
                          .boolean()
                          .describe(
                            "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                          )
                          .optional(),
                      })
                      .describe('Options for the CATEGORY_FIELD plugin.'),
                  }),
                ])
              )
            )
            .max(100)
            .optional(),
          condition: z
            .object({
              groupName: z
                .string()
                .describe(
                  'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                )
                .max(100)
                .optional()
                .nullable(),
              permission: z.enum(['ANYONE', 'RESTRICTED']).optional(),
            })
            .describe(
              'This indicates that this fields is conditional and is not retrieved by default.'
            )
            .optional(),
        }),
        z.xor([
          z.object({
            numberRange: z.never().optional(),
            stringLengthRange: z.never().optional(),
            arraySizeRange: z.never().optional(),
          }),
          z.object({
            stringLengthRange: z.never().optional(),
            arraySizeRange: z.never().optional(),
            numberRange: z
              .object({
                min: z
                  .number()
                  .describe(
                    'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                  )
                  .optional()
                  .nullable(),
                max: z
                  .number()
                  .describe(
                    'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Range of possible values for a numerical field.'),
          }),
          z.object({
            numberRange: z.never().optional(),
            arraySizeRange: z.never().optional(),
            stringLengthRange: z
              .object({
                minLength: z
                  .number()
                  .int()
                  .describe(
                    'Minimum permitted length for a text field.\n\nDefault: No validation'
                  )
                  .optional()
                  .nullable(),
                maxLength: z
                  .number()
                  .int()
                  .describe(
                    'Maximum permitted length for a text field.\n\nDefault: No validation'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
              ),
          }),
          z.object({
            numberRange: z.never().optional(),
            stringLengthRange: z.never().optional(),
            arraySizeRange: z
              .object({
                minSize: z
                  .number()
                  .int()
                  .describe(
                    'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                  )
                  .optional()
                  .nullable(),
                maxSize: z
                  .number()
                  .int()
                  .describe(
                    'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
              ),
          }),
        ])
      )
      .describe('Field to update.'),
  });
})();
export const PatchDataCollectionFieldResponse = (() => {
  let comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema: z.ZodType<any> =
    z.intersection(
      z.object({}),
      z.xor([
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
        }),
        z.object({
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          reference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
            })
            .describe('Metadata for a reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          multiReference: z
            .object({
              referencedCollectionId: z
                .string()
                .describe('Referenced collection ID.')
                .max(256)
                .optional(),
              referencingFieldKey: z
                .string()
                .describe('Referencing field ID.')
                .max(1000)
                .optional(),
              referencingDisplayName: z
                .string()
                .describe('Display name in the CMS for the referenced data.')
                .max(1000)
                .optional(),
            })
            .describe('Metadata for a multi-reference field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          array: z.never().optional(),
          pageLink: z.never().optional(),
          object: z
            .object({
              fields: z
                .array(
                  z.object({
                    key: z.string().describe('Field ID.').max(1000).optional(),
                    displayName: z
                      .string()
                      .describe('Display name for the field.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'TEXT',
                        'NUMBER',
                        'DATE',
                        'DATETIME',
                        'IMAGE',
                        'BOOLEAN',
                        'DOCUMENT',
                        'URL',
                        'RICH_TEXT',
                        'VIDEO',
                        'ANY',
                        'ARRAY_STRING',
                        'ARRAY_DOCUMENT',
                        'AUDIO',
                        'TIME',
                        'LANGUAGE',
                        'RICH_CONTENT',
                        'MEDIA_GALLERY',
                        'ADDRESS',
                        'PAGE_LINK',
                        'REFERENCE',
                        'MULTI_REFERENCE',
                        'OBJECT',
                        'ARRAY',
                        'LEGACY_TIME',
                        'LEGACY_BOOK',
                        'LEGACY_EXTERNAL_URL',
                        'LEGACY_BROKEN_REFERENCE',
                        'LEGACY_IMAGE',
                        'LEGACY_COLOR',
                        'LEGACY_EXTERNAL_VIDEO',
                        'EMAIL',
                      ])
                      .describe(
                        'Field type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data).'
                      )
                      .optional(),
                    typeMetadata: z
                      .lazy(
                        () =>
                          comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                      )
                      .describe(
                        'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                      )
                      .optional(),
                    capabilities: z
                      .object({
                        sortable: z
                          .boolean()
                          .describe(
                            'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                          )
                          .optional(),
                        queryOperators: z
                          .array(
                            z.enum([
                              'EQ',
                              'LT',
                              'GT',
                              'NE',
                              'LTE',
                              'GTE',
                              'STARTS_WITH',
                              'ENDS_WITH',
                              'CONTAINS',
                              'HAS_SOME',
                              'HAS_ALL',
                              'EXISTS',
                              'URLIZED',
                            ])
                          )
                          .max(100)
                          .optional(),
                      })
                      .describe('Capabilities the object field supports.')
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
            })
            .describe('Metadata for an object field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          pageLink: z.never().optional(),
          array: z
            .object({
              elementType: z
                .enum([
                  'TEXT',
                  'NUMBER',
                  'DATE',
                  'DATETIME',
                  'IMAGE',
                  'BOOLEAN',
                  'DOCUMENT',
                  'URL',
                  'RICH_TEXT',
                  'VIDEO',
                  'ANY',
                  'ARRAY_STRING',
                  'ARRAY_DOCUMENT',
                  'AUDIO',
                  'TIME',
                  'LANGUAGE',
                  'RICH_CONTENT',
                  'MEDIA_GALLERY',
                  'ADDRESS',
                  'PAGE_LINK',
                  'REFERENCE',
                  'MULTI_REFERENCE',
                  'OBJECT',
                  'ARRAY',
                  'LEGACY_TIME',
                  'LEGACY_BOOK',
                  'LEGACY_EXTERNAL_URL',
                  'LEGACY_BROKEN_REFERENCE',
                  'LEGACY_IMAGE',
                  'LEGACY_COLOR',
                  'LEGACY_EXTERNAL_VIDEO',
                  'EMAIL',
                ])
                .describe("Element's data type.")
                .optional(),
              typeMetadata: z
                .lazy(
                  () =>
                    comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                )
                .describe(
                  'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                )
                .optional(),
            })
            .describe('Metadata for an array field.'),
        }),
        z.object({
          reference: z.never().optional(),
          multiReference: z.never().optional(),
          object: z.never().optional(),
          array: z.never().optional(),
          pageLink: z
            .object({
              calculator: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z.never().optional(),
                    }),
                    z.object({
                      urlizedOnlyPattern: z.never().optional(),
                      fieldsPattern: z
                        .object({
                          pattern: z.string().max(1000).optional(),
                          lowercase: z.boolean().optional(),
                        })
                        .describe(
                          'Value is calculated according to pattern, whitespaces are replaced with dash [-].'
                        ),
                    }),
                    z.object({
                      fieldsPattern: z.never().optional(),
                      urlizedOnlyPattern: z
                        .object({ pattern: z.string().max(1000).optional() })
                        .describe('Value is only URL encoded.'),
                    }),
                  ])
                )
                .optional(),
              linkedRouterPage: z
                .string()
                .describe(
                  'Defines reference to router pattern in the site document.'
                )
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe('Metadata for a page link field.'),
        }),
      ])
    );
  return z.object({
    dataCollection: z
      .object({
        _id: z
          .string()
          .describe(
            '[Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.\nFor [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.'
          )
          .max(256)
          .optional(),
        collectionType: z
          .enum(['NATIVE', 'WIX_APP', 'BLOCKS_APP', 'EXTERNAL'])
          .describe(
            'Collection type. Indicates how the collection was created and how it is stored.'
          )
          .optional(),
        ownerAppId: z
          .string()
          .describe(
            'ID of the app that defined this collection. For collections defined by Wix users, this value is null.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        maxPageSize: z
          .number()
          .int()
          .describe(
            "Maximum number of items returned in a single query, based on the underlying storage.\nNative collections have a maximum page size of 1000 for offset-based queries or 100 for cursor-based queries.\nExternal collections' maximum page size defaults to 50, but an external provider can set any maximum value up to 1000."
          )
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe(
            "Collection's display name as shown in the CMS. For example, `My First Collection`."
          )
          .max(1000)
          .optional()
          .nullable(),
        defaultDisplayOrder: z
          .object({
            fieldKey: z
              .string()
              .describe('Field to sort by.')
              .max(1000)
              .optional(),
            direction: z
              .enum(['ASC', 'DESC'])
              .describe(
                'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
              )
              .optional(),
          })
          .describe(
            "Default item sorting order when a query doesn't specify one."
          )
          .optional(),
        displayNamespace: z
          .string()
          .describe(
            'UI-friendly namespace of the Wix app with which the data collection is associated, such as Stores or Bookings.\nEmpty for all data collections not owned by Wix apps.'
          )
          .max(1000)
          .optional()
          .nullable(),
        displayField: z
          .string()
          .describe(
            'Field whose value the CMS displays to represent the collection item when referenced in a different collection.'
          )
          .max(1000)
          .optional()
          .nullable(),
        capabilities: z
          .object({
            dataOperations: z
              .array(
                z.enum([
                  'AGGREGATE',
                  'BULK_INSERT',
                  'BULK_REMOVE',
                  'BULK_SAVE',
                  'BULK_UPDATE',
                  'COUNT',
                  'DISTINCT',
                  'FIND',
                  'GET',
                  'INSERT',
                  'INSERT_REFERENCE',
                  'IS_REFERENCED',
                  'QUERY_REFERENCED',
                  'REMOVE',
                  'REMOVE_REFERENCE',
                  'REPLACE_REFERENCES',
                  'SAVE',
                  'TRUNCATE',
                  'UPDATE',
                  'PATCH',
                  'BULK_PATCH',
                ])
              )
              .max(100)
              .optional(),
            collectionOperations: z
              .array(z.enum(['UPDATE', 'REMOVE']))
              .max(100)
              .optional(),
            indexLimits: z
              .object({
                regular: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular (non-unique) indexes allowed for this collection.'
                  )
                  .optional(),
                unique: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of unique indexes allowed for this collection.'
                  )
                  .optional(),
                total: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of regular and unique indexes allowed for this collection.'
                  )
                  .optional(),
              })
              .describe('Maximum number of indexes for the collection.')
              .optional(),
          })
          .describe(
            'Capabilities the collection supports.\nCapabilities are set by default. Values depend on the collection type.'
          )
          .optional(),
        fields: z
          .array(
            z.intersection(
              z.object({
                key: z
                  .string()
                  .describe(
                    'Unique identifier for the field. For example, `firstName`.'
                  )
                  .max(1000)
                  .optional(),
                displayName: z
                  .string()
                  .describe(
                    "Field's display name when displayed in the CMS. For example, `First Name`."
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'TEXT',
                    'NUMBER',
                    'DATE',
                    'DATETIME',
                    'IMAGE',
                    'BOOLEAN',
                    'DOCUMENT',
                    'URL',
                    'RICH_TEXT',
                    'VIDEO',
                    'ANY',
                    'ARRAY_STRING',
                    'ARRAY_DOCUMENT',
                    'AUDIO',
                    'TIME',
                    'LANGUAGE',
                    'RICH_CONTENT',
                    'MEDIA_GALLERY',
                    'ADDRESS',
                    'PAGE_LINK',
                    'REFERENCE',
                    'MULTI_REFERENCE',
                    'OBJECT',
                    'ARRAY',
                    'LEGACY_TIME',
                    'LEGACY_BOOK',
                    'LEGACY_EXTERNAL_URL',
                    'LEGACY_BROKEN_REFERENCE',
                    'LEGACY_IMAGE',
                    'LEGACY_COLOR',
                    'LEGACY_EXTERNAL_VIDEO',
                    'EMAIL',
                  ])
                  .describe(
                    "Field's data type.\n\nLearn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data)."
                  )
                  .optional(),
                typeMetadata:
                  comWixpressCloudDataApiCollectionserviceFieldTypeMetadataSchema
                    .describe(
                      'Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays.'
                    )
                    .optional(),
                systemField: z
                  .boolean()
                  .describe('Whether the field is a system field.')
                  .optional(),
                capabilities: z
                  .object({
                    sortable: z
                      .boolean()
                      .describe(
                        'Whether the collection can be sorted by this field.\n\nDefault: `false`'
                      )
                      .optional(),
                    queryOperators: z
                      .array(
                        z.enum([
                          'EQ',
                          'LT',
                          'GT',
                          'NE',
                          'LTE',
                          'GTE',
                          'STARTS_WITH',
                          'ENDS_WITH',
                          'CONTAINS',
                          'HAS_SOME',
                          'HAS_ALL',
                          'EXISTS',
                          'URLIZED',
                        ])
                      )
                      .max(100)
                      .optional(),
                  })
                  .describe('Capabilities the field supports.')
                  .optional(),
                encrypted: z
                  .boolean()
                  .describe('Whether the field is encrypted.')
                  .optional(),
                description: z
                  .string()
                  .describe('Field description.')
                  .max(16000)
                  .optional()
                  .nullable(),
                readOnly: z
                  .boolean()
                  .describe(
                    "Whether the field is read-only. A read-only field can't be changed.\n\nDefault: `false`"
                  )
                  .optional()
                  .nullable(),
                immutable: z
                  .boolean()
                  .describe(
                    'Whether the field is immutable. An immutable field can be set once, but then cannot be updated.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                required: z
                  .boolean()
                  .describe(
                    'Whether the field is required.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                plugins: z
                  .array(
                    z.intersection(
                      z.object({ type: z.enum(['UNKNOWN', 'CMS']).optional() }),
                      z.xor([
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z.never().optional(),
                        }),
                        z.object({
                          categoryFieldOptions: z.never().optional(),
                          cmsOptions: z
                            .object({
                              internal: z
                                .boolean()
                                .describe(
                                  'Indicates an internal CMS field. The CMS does not display internal fields.\n\nDefault: `false`'
                                )
                                .optional(),
                            })
                            .describe('Options for the CMS plugin.'),
                        }),
                        z.object({
                          cmsOptions: z.never().optional(),
                          categoryFieldOptions: z
                            .object({
                              isPrimary: z
                                .boolean()
                                .describe(
                                  "Transient input-only flag. When set to `true` during create/update, designates\nthis category field's group as the target for `_primaryCategory`. The flag is\nnot persisted; `_primaryCategory` remains the source of truth.\n\nDefault: `false`"
                                )
                                .optional(),
                            })
                            .describe('Options for the CATEGORY_FIELD plugin.'),
                        }),
                      ])
                    )
                  )
                  .max(100)
                  .optional(),
                condition: z
                  .object({
                    groupName: z
                      .string()
                      .describe(
                        'If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    permission: z
                      .enum(['ANYONE', 'RESTRICTED'])
                      .describe(
                        'If present, the field may be omitted from the results, if the requester lacks sufficient permissions to access data stored in the field.'
                      )
                      .optional(),
                  })
                  .describe(
                    'This indicates that this fields is conditional and is not retrieved by default.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                }),
                z.object({
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  numberRange: z
                    .object({
                      min: z
                        .number()
                        .describe(
                          'Minimum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      max: z
                        .number()
                        .describe(
                          'Maximum permitted value for a numerical field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Range of possible values for a numerical field.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  arraySizeRange: z.never().optional(),
                  stringLengthRange: z
                    .object({
                      minLength: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxLength: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted length for a text field.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`.'
                    ),
                }),
                z.object({
                  numberRange: z.never().optional(),
                  stringLengthRange: z.never().optional(),
                  arraySizeRange: z
                    .object({
                      minSize: z
                        .number()
                        .int()
                        .describe(
                          'Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                      maxSize: z
                        .number()
                        .int()
                        .describe(
                          'Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.\n\nDefault: No validation'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.'
                    ),
                }),
              ])
            )
          )
          .max(1000)
          .optional(),
        permissions: z
          .object({
            insert: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to insert items into the collection.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            update: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to update collection items.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            remove: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to remove items from the collection.\n\nDefault: `CMS_EDITOR`'
              )
              .optional(),
            read: z
              .enum(['ADMIN', 'SITE_MEMBER_AUTHOR', 'SITE_MEMBER', 'ANYONE'])
              .describe(
                'Role required to read collection items.\n\nDefault: `SITE_MEMBER_AUTHOR`'
              )
              .optional(),
          })
          .describe(
            '[Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest role needed to perform each action.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            "Collection's current revision number, which increments each time the collection is updated. For an update operation to succeed, you must specify the latest revision number."
          )
          .optional()
          .nullable(),
        plugins: z
          .array(
            z.intersection(
              z.object({
                type: z
                  .enum([
                    'PUBLISH',
                    'SINGLE_ITEM',
                    'URLIZED',
                    'GRIDAPPLESS',
                    'MULTILINGUAL',
                    'SHARED',
                    'EDITABLE_PAGE_LINK',
                    'CMS',
                  ])
                  .describe('Plugin type.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                }),
                z.object({
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  publishOptions: z
                    .object({
                      defaultStatus: z
                        .enum(['PUBLISHED', 'DRAFT'])
                        .describe('Default status.')
                        .optional(),
                    })
                    .describe(
                      'Options for the Publish plugin.\nThis plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.'
                    ),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  singleItemOptions: z
                    .object({
                      singleItemId: z
                        .string()
                        .describe(
                          'ID of the single item in this collection. If you insert or update an item, its ID value is always changed to this.'
                        )
                        .max(16000)
                        .optional(),
                    })
                    .describe('Options for the Single Item plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  urlizedOptions: z
                    .object({
                      format: z
                        .enum(['ORIGINAL', 'PLAIN'])
                        .describe(
                          'Encoding method for generating a URL in ASCII characters.'
                        )
                        .optional(),
                    })
                    .describe('Options for the Urlized plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  multilingualOptions: z
                    .object({
                      translatableFieldKeys: z
                        .array(z.string())
                        .max(1000)
                        .optional(),
                    })
                    .describe('Options for the Multilingual plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  cmsOptions: z.never().optional(),
                  editablePageLinkOptions: z
                    .object({
                      isPersisted: z.boolean().optional(),
                      isMutable: z.boolean().optional(),
                    })
                    .describe('Options for the PageLink plugin.'),
                }),
                z.object({
                  publishOptions: z.never().optional(),
                  singleItemOptions: z.never().optional(),
                  urlizedOptions: z.never().optional(),
                  multilingualOptions: z.never().optional(),
                  editablePageLinkOptions: z.never().optional(),
                  cmsOptions: z
                    .object({
                      siteSort: z
                        .object({
                          sort: z
                            .array(
                              z.object({
                                fieldKey: z
                                  .string()
                                  .describe('Field to sort by.')
                                  .max(1000)
                                  .optional(),
                                direction: z
                                  .enum(['ASC', 'DESC'])
                                  .describe(
                                    'Sort order. Use `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `ASC`'
                                  )
                                  .optional(),
                              })
                            )
                            .max(100)
                            .optional(),
                        })
                        .describe(
                          'CMS sort, applied when a collection is displayed on a site.'
                        )
                        .optional(),
                    })
                    .describe('Options for the CMS plugin.'),
                }),
              ])
            )
          )
          .max(100)
          .optional(),
        pagingModes: z
          .array(z.enum(['OFFSET', 'CURSOR']))
          .max(2)
          .optional(),
        _createdDate: z
          .date()
          .describe('Date the collection was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date the collection was last updated.')
          .optional()
          .nullable(),
      })
      .describe('Updated data collection.')
      .optional(),
  });
})();
